import { t as uiText } from '../i18n';
// src/pages/Home.tsx
import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense } from 'react';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import Layout from '../components/Layout';
import AiChatSidebarContent from '../components/AiChatSidebarContent';
import AgentHomePage from './AgentHomePage';
import ManagementPanel from '../components/ManagementPanel';
import { OptionalHomeTabGroup } from '../components/HomeTabGroup';
import type { HomeTabKey } from './AgentHomePage';
import AgentChatSidebarContent from '../components/AgentChatSidebarContent';
import type { AgentChatSessionEntry } from '../components/AgentChatSidebarContent';
import BoardList from '../components/BoardList';
import ArchivedBoardList from '../components/ArchivedBoardList';
import HashtagListView from '../components/HashtagListView';
import FileListView from '../components/FileListView';
import NewWorksModal from '../components/NewWorksModal';
import { hasNcgApplyPending, NCG_APPLY_PATH } from '../features/signup/ncgApplyValidation';
import noteIcon from '../assets/image/icon/note.png';
import threadIcon from '../assets/image/icon/thread.png';
import calendarIcon from '../assets/image/icon/calendar.png';
import filesIcon from '../assets/image/icon/files.png';
import hashtagIcon from '../assets/image/icon/hashtag.png';
import newpageIcon from '../assets/image/icon/newpage.png';
import searchIcon from '../assets/image/icon/search.png';
// 公開設定用のアイコンを追加（既存のアイコンを流用）
import publicIcon from '../assets/image/icon/newpage.png';
import RAGSearchInterface from '../components/RAGSearchInterface';
import PublicPageManager from '../components/PublicPageManager';
import PublicPublishButton from '../components/PublicPublishButton';
import PublicPageSummary from '../components/PublicPageSummary';
import FaviconUploader from '../components/FaviconUploader';
import TabTitleInput from '../components/TabTitleInput';
import HeaderSettings from '../components/HeaderSettings';
import FloatingWidgetSettings from '../components/FloatingWidgetSettings';
import CrawlerSettings from '../components/CrawlerSettings';
import CrawlerDashboard from '../components/CrawlerDashboard';
import CreditUsageAlert from '../components/CreditUsageAlert';

import { useWorkspaceContext } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { useArchiveContext } from '../context/ArchiveContext';
import { useHashtagContext } from '../context/HashtagContext';
import { useSearchContext } from '../context/SearchContext';
import { useBoardListContext } from '../context/BoardListContext';
import { handleArchivedBoardHashtags } from '../features/hashtag/hashtagUtils';
import DeleteUntitledModal from '../components/DeleteUntitledModal';
import BackupModal from '../components/BackupModal';
import { deleteNoContentMemos } from '../features/workspace/deleteNoContentMemos';
import { BoardData as ApiBoardData } from '../api/boards';
import NotePreviewCard from '../components/NotePreviewCard';
import PublishAxesMenu, { type AxisState } from '../components/PublishAxesMenu';
import { requestDeepReasoningSuggestionsWithBatching } from '../api/deepReasoning';
import './Home.css';
import { useIsWorkspaceAdminQuery } from '../features/auth/queries/workspaceRoleQueries';
import FolderMoveModal from '../components/FolderMoveModal';
import { movePagesToFolder, getFolderName } from '../features/folder/movePagesToFolder';
import { useFolderContext } from '../context/FolderContext';
import ExportSelectedButton from '../components/ExportSelectedButton';
import ImportPagesModal from '../components/ImportPagesModal';
import { HomeMenu } from '../components/HomeMenu';
import { lazyWithRetry, lazyWithRetryNamed } from '../utils/lazyWithRetry';
import { isNcgSeries } from '../features/plans/series';
import { useWorkspaceEntitlements } from '../features/plans/useWorkspaceEntitlements';
import { isAgentTabVisible, resolveWorkspaceHomeLanding } from '../features/agent/agentTabVisibility';
import {
  legacyWorkspaceTabPath,
  parseWorkspaceHomePath,
  readThreadIdFromLocation,
  readWorkspaceTabFromLocation,
  workspaceDashboardPath,
  workspaceHomeLandingPath,
  workspacePageListPath,
  workspacePagePath,
  workspaceThreadsPath,
} from '../utils/workspaceNavigation';

const LazyThreadsTabView = lazyWithRetry(() => import('../components/ThreadsTabView'));
const LazyDRSSection = lazyWithRetry(() => import('../components/DRSSection'));
const LazyPublicSettings = lazyWithRetry(() => import('../components/PublicSettings'));
const LazyOGASetting = lazyWithRetry(() => import('../components/OGASetting'));
const LazyRAGAnalyticsDashboard = lazyWithRetryNamed(
  () => import('../components/RAGAnalyticsDashboard'),
  'RAGAnalyticsDashboard'
);
const LazyTokenUsageDashboard = lazyWithRetryNamed(
  () => import('../components/TokenUsageDashboard'),
  'TokenUsageDashboard'
);

const TabLoading: React.FC<{ label?: string }> = ({ label }) => {
  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 14, color: '#666' }}>
        {uiText("Loading")}{label ? `: ${label}` : ''}...
      </div>
    </div>
  );
};

// 日付データの型定義
interface DateData {
  year: number;
  month: number;
  day: number;
}

// 日付インジケーターコンポーネント
const DateIndicator: React.FC<{ count: number; isSelected: boolean }> = ({ count, isSelected }) => {
  // 常に同じ高さの領域を確保
  return (
    <div style={{
      height: '8px', // 固定の高さを確保
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: '2px'
    }}>
      {count > 0 && (
        <>
          {count <= 3 ? (
            // 3個までは点で表示
            (<div style={{
              display: 'flex',
              gap: '2px',
              alignItems: 'center'
            }}>
              {Array.from({ length: count }, (_, i) => (
                <div
                  key={i}
                  style={{
                    width: '4px',
                    height: '4px',
                    borderRadius: '50%',
                    backgroundColor: isSelected ? 'white' : 'var(--workspace-primary, #cfae91)'
                  }}
                />
              ))}
            </div>)
          ) : (
            // 4個以上は横棒で表示
            (<div
              style={{
                width: '12px',
                height: '3px',
                borderRadius: '2px',
                backgroundColor: isSelected ? 'white' : 'var(--workspace-primary, #cfae91)'
              }}
            />)
          )}
        </>
      )}
    </div>
  );
};

// カレンダーコンポーネント
const CalendarComponent: React.FC<{ 
  dateData: DateData[]; 
  boards: ApiBoardData[]; 
  onDateSelect: (date: Date | null) => void;
  selectedDate: Date | null;
}> = ({ dateData, boards, onDateSelect, selectedDate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // 月の最初の日と最後の日を取得
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  // 月の名前を取得
  const monthNames = [
    uiText("January"), uiText("February"), uiText("March"), uiText("April"), uiText("May"), uiText("June"),
    uiText("July"), uiText("August"), uiText("September"), uiText("October"), uiText("November"), uiText("December")
  ];

  // 曜日の名前
  const dayNames = [uiText("Sun"), uiText("Mon"), uiText("Tue"), uiText("Wed"), uiText("Thu"), uiText("Fri"), uiText("Sat")];

  // 前の月に移動
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1));
  };

  // 次の月に移動
  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1));
  };

  // 今日に戻る
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // 日付をクリック
  const handleDateClick = (day: number) => {
    const clickedDate = new Date(year, month, day);
    onDateSelect(clickedDate);
  };

  // 指定された日付のデータ数を取得
  const getDateCount = (day: number) => {
    const matchingData = dateData.filter(data => 
      data.year === year && 
      data.month === month + 1 && 
      data.day === day
    );

    return matchingData.length;
  };

  // カレンダーの日付配列を生成
  const calendarDays = [];

  // 前月の日付（空白部分）
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }

  // 今月の日付
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      {/* ヘッダー */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '0 10px'
      }}>
        <button
          onClick={goToPreviousMonth}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '4px',
            color: '#666'
          }}
        >
          ‹
        </button>
        
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ 
            margin: '0', 
            fontSize: '20px', 
            fontWeight: 'bold',
            color: '#333'
          }}>
            {year}{uiText("yearSuffix")}{monthNames[month]}
          </h2>
          <button
            onClick={goToToday}
            style={{
              background: 'var(--workspace-primary, #cfae91)',
              color: 'white',
              border: 'none',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              marginTop: '4px'
            }}
          >
            {uiText("Today")}
          </button>
        </div>
        
        <button
          onClick={goToNextMonth}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '4px',
            color: '#666'
          }}
        >
          ›
        </button>
      </div>

      {/* 曜日ヘッダー */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '1px',
        marginBottom: '10px'
      }}>
        {dayNames.map((dayName, index) => (
          <div
            key={index}
            style={{
              padding: '10px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '14px',
              color: index === 0 ? '#e74c3c' : index === 6 ? '#3498db' : '#333',
              backgroundColor: '#f8f9fa'
            }}
          >
            {dayName}
          </div>
        ))}
      </div>

      {/* カレンダーグリッド */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '1px',
        backgroundColor: '#e0e0e0'
      }}>
        {calendarDays.map((day, index) => {
          if (day === null) {
            return (
              <div
                key={index}
                style={{
                  height: '40px',
                  backgroundColor: 'white'
                }}
              />
            );
          }

          const isToday = 
            day === today.getDate() && 
            month === today.getMonth() && 
            year === today.getFullYear();
          
          const isSelected = selectedDate && 
            day === selectedDate.getDate() && 
            month === selectedDate.getMonth() && 
            year === selectedDate.getFullYear();

          return (
            <button
              key={index}
              onClick={() => handleDateClick(day)}
              style={{
                height: '40px',
                border: 'none',
                backgroundColor: isSelected ? 'var(--workspace-primary, #cfae91)' : isToday ? 'var(--workspace-border-soft, #f5f0eb)' : 'white',
                color: isSelected ? 'white' : isToday ? 'var(--workspace-primary, #cfae91)' : '#333',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: isToday ? 'bold' : 'normal',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!isSelected && !isToday) {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected && !isToday) {
                  e.currentTarget.style.backgroundColor = 'white';
                }
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span>{day}</span>
                <DateIndicator count={getDateCount(day)} isSelected={!!isSelected} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Home: React.FC = () => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, initializing: authInitializing } = useAuth();

  const shouldShowAISearchTab = true;

  const { 
    workspaces, 
    loading
  } = useWorkspaceContext();
  const selectedWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === routeWorkspaceId),
    [workspaces, routeWorkspaceId]
  );

  // /home（workspaceIdなし）に来た場合は、最後に開いたワークスペースへ即遷移する
  // - localStorage は「入口の既定値」に限定し、以降の読書き先決定には使わない
  useEffect(() => {
    if (routeWorkspaceId) return; // /:workspaceId 配下では何もしない
    if (loading) return;
    if (!workspaces || workspaces.length === 0) return;

    let last: string | null = null;
    try {
      last = window.localStorage?.getItem('lastWorkspaceId') || null;
    } catch {
      last = null;
    }

    const fallbackId = workspaces[0]?.id;
    const targetId = last && workspaces.some((w) => w.id === last) ? last : fallbackId;
    if (!targetId) return;

    navigate(`/${targetId}`, { replace: true });
  }, [routeWorkspaceId, loading, workspaces, navigate]);

  // デバッグ: ワークスペースコンテキストの状態を監視
  useEffect(() => {}, [workspaces, loading, routeWorkspaceId]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedBoardIds, setSelectedBoardIds] = useState<string[]>([]);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState(0);
  const [publishingPageCount, setPublishingPageCount] = useState(0);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [unpublishProgress, setUnpublishProgress] = useState(0);
  const [unpublishingPageCount, setUnpublishingPageCount] = useState(0);
  // 公開設定（知識軸 / ページ軸）の一括切り替え
  const [showPublishMenu, setShowPublishMenu] = useState(false);
  const [isSettingWebEnabled, setIsSettingWebEnabled] = useState(false);
  const [htmlPagesEnabledForWs, setHtmlPagesEnabledForWs] = useState<boolean | null>(null);
  const [isHashtagMode, setIsHashtagMode] = useState(false);
  const [isFileMode, setIsFileMode] = useState(false);
  const [isCalendarMode, setIsCalendarMode] = useState(false);
  const [isRAGMode, setIsRAGMode] = useState(false);
  const [aiChatNewSessionRequest, setAiChatNewSessionRequest] = useState(0);
  const [aiChatActiveSessionId, setAiChatActiveSessionId] = useState<string | null>(null);
  const [aiChatSessionSelection, setAiChatSessionSelection] = useState<{
    sessionId: string;
    requestId: number;
  } | null>(null);
  const [aiChatResolvedSessionTitle, setAiChatResolvedSessionTitle] = useState<{
    sessionId: string;
    title: string;
    requestId: number;
  } | null>(null);
  const [aiChatHistoryRevalidateRequest, setAiChatHistoryRevalidateRequest] = useState(0);
  const [isPublicMode, setIsPublicMode] = useState(false);
  const [isSuggestMode, setIsSuggestMode] = useState(false);
  const [isOGAMode, setIsOGAMode] = useState(false);
  const [isDashboardMode, setIsDashboardMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return readWorkspaceTabFromLocation(window.location.pathname, window.location.search) === 'dashboard';
  });
  const [isTokenMode, setIsTokenMode] = useState(false);
  const [isThreadMode, setIsThreadMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return readWorkspaceTabFromLocation(window.location.pathname, window.location.search) === 'threads';
  });
  const [isAgentHomeMode, setIsAgentHomeMode] = useState(false);
  const [isManagementPanelOpen, setIsManagementPanelOpen] = useState(false);
  const [tabGroupOpen, setTabGroupOpen] = useState({
    agent: true,
    pages: false,
    rag: false,
  });
  const prevTabGroupRef = useRef<'agent' | 'pages' | 'rag' | null>(null);
  const seenWorkspaceIdRef = useRef<string | null>(null);
  /** カレンダーなど、専用パスを持たないタブ。`/:workspaceId` にいるあいだ着地リダイレクトを止める。 */
  const nonRoutedTabRef = useRef<HomeTabKey | null>(null);
  const [openThreadId, setOpenThreadId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return readThreadIdFromLocation(window.location.pathname, window.location.search);
  });
  // Agent Chat サイドバー用 state
  const [agentChatSelectedSession, setAgentChatSelectedSession] = useState<AgentChatSessionEntry | null>(null);
  const [agentChatNewChatRequest, setAgentChatNewChatRequest] = useState(0);
  const [drsOpenSubTab, setDrsOpenSubTab] = useState<'suggest' | 'keyword' | null>(null);
  const [drsOpenRequestId, setDrsOpenRequestId] = useState<number>(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sortBy, setSortBy] = useState<'lastModified' | 'createdAt' | 'title' | 'contentSize'>('lastModified');
  const [currentBoards, setCurrentBoards] = useState<ApiBoardData[]>([]);
  const { data: isAdmin = false, isFetched: isAdminFetched } = useIsWorkspaceAdminQuery(routeWorkspaceId ?? '', !!routeWorkspaceId);
  const { folders } = useFolderContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAdminFetched || isAdmin) return;
    setIsDashboardMode(false);
  }, [isAdmin, isAdminFetched]);

  // 他のモードが true になったら isAgentHomeMode を自動クリアする
  useEffect(() => {
    if (isThreadMode || isCalendarMode || isRAGMode || isFileMode || isHashtagMode ||
        isPublicMode || isSuggestMode || isOGAMode || (isDashboardMode && isAdmin) || isTokenMode) {
      setIsAgentHomeMode(false);
    }
  }, [isThreadMode, isCalendarMode, isRAGMode, isFileMode, isHashtagMode, isPublicMode, isSuggestMode, isOGAMode, isDashboardMode, isAdmin, isTokenMode]);

  const applyHomeTab = useCallback((tab: HomeTabKey, threadId: string | null = null) => {
    setIsAgentHomeMode(false);
    setIsHashtagMode(false);
    setIsFileMode(false);
    setIsCalendarMode(false);
    setIsRAGMode(false);
    setIsPublicMode(false);
    setIsSuggestMode(false);
    setIsOGAMode(false);
    setIsDashboardMode(false);
    setIsTokenMode(false);
    setIsThreadMode(false);
    setOpenThreadId(tab === 'thread' ? threadId : null);
    if (tab === 'thread') setIsThreadMode(true);
    else if (tab === 'calendar') setIsCalendarMode(true);
    else if (tab === 'rag') setIsRAGMode(true);
    else if (tab === 'file') setIsFileMode(true);
    else if (tab === 'hashtag') setIsHashtagMode(true);
    else if (tab === 'public') setIsPublicMode(true);
    else if (tab === 'suggest') setIsSuggestMode(true);
    else if (tab === 'oga') setIsOGAMode(true);
    else if (tab === 'dashboard') setIsDashboardMode(true);
    else if (tab === 'token') setIsTokenMode(true);
    else if (tab === 'home') {
      if (isAgentTabVisible(undefined, selectedWorkspace?.planId)) setIsAgentHomeMode(true);
      else setIsDashboardMode(true);
    }
  }, [selectedWorkspace?.planId]);

  const openWorkspaceTab = useCallback((tab: HomeTabKey) => {
    setIsSelectionMode(false);
    setSelectedBoardIds([]);
    if (!routeWorkspaceId) {
      applyHomeTab(tab);
      return;
    }

    const rootPath = `/${routeWorkspaceId}`;
    if (tab === 'dashboard' || tab === 'page' || tab === 'thread') {
      nonRoutedTabRef.current = null;
      const target = tab === 'dashboard'
        ? workspaceDashboardPath(routeWorkspaceId)
        : tab === 'page'
          ? workspacePageListPath(routeWorkspaceId, {
            embed: new URLSearchParams(location.search).get('embed') === 'true',
          })
          : workspaceThreadsPath(routeWorkspaceId);
      const targetUrl = new URL(target, 'http://local');
      applyHomeTab(tab === 'page' ? 'page' : tab, null);
      if (location.pathname !== targetUrl.pathname || location.search !== targetUrl.search) {
        navigate(target);
      }
      return;
    }

    if (tab === 'home') {
      nonRoutedTabRef.current = null;
      const landing = selectedWorkspace
        ? resolveWorkspaceHomeLanding({
          planId: selectedWorkspace.planId,
          isAdmin,
          isNcg: isNcgSeries(selectedWorkspace.seriesId),
        })
        : 'agent';
      const target = workspaceHomeLandingPath(routeWorkspaceId, landing);
      const targetPath = target.split('?')[0];
      if (location.pathname === targetPath && !location.search) {
        applyHomeTab(landing === 'dashboard' ? 'dashboard' : landing === 'pages' ? 'page' : 'home');
        return;
      }
      navigate(target);
      return;
    }

    nonRoutedTabRef.current = tab;
    applyHomeTab(tab);
    if (location.pathname !== rootPath || location.search) {
      navigate({ pathname: rootPath, search: '' });
    }
  }, [
    applyHomeTab,
    isAdmin,
    location.pathname,
    location.search,
    navigate,
    routeWorkspaceId,
    selectedWorkspace,
  ]);

  // パスがタブの正本。旧 `?tab=` は同じ画面のパスへ置き換える。
  useEffect(() => {
    if (!routeWorkspaceId) return;

    if (seenWorkspaceIdRef.current !== routeWorkspaceId) {
      seenWorkspaceIdRef.current = routeWorkspaceId;
      nonRoutedTabRef.current = null;
      setTabGroupOpen({ agent: true, pages: false, rag: false });
      prevTabGroupRef.current = null;
      setIsSelectionMode(false);
      setSelectedBoardIds([]);
    }

    const legacyPath = legacyWorkspaceTabPath(routeWorkspaceId, location.search);
    if (legacyPath) {
      const legacyUrl = new URL(legacyPath, 'http://local');
      const samePath = legacyUrl.pathname === location.pathname && legacyUrl.search === location.search;
      if (!samePath) {
        navigate(legacyPath, { replace: true });
        return;
      }
    }

    const parsed = parseWorkspaceHomePath(location.pathname, routeWorkspaceId);
    if (!parsed) return;

    if (parsed.section === 'dashboard') {
      if (!isAdminFetched) return;
      if (!isAdmin) {
        navigate(workspacePageListPath(routeWorkspaceId), { replace: true });
        return;
      }
      nonRoutedTabRef.current = null;
      applyHomeTab('dashboard');
      return;
    }

    if (parsed.section === 'pages') {
      nonRoutedTabRef.current = null;
      applyHomeTab('page');
      return;
    }

    if (parsed.section === 'threads') {
      nonRoutedTabRef.current = null;
      applyHomeTab('thread', parsed.threadId);
      return;
    }

    if (sessionStorage.getItem('notreu.openTab') === 'file') {
      sessionStorage.removeItem('notreu.openTab');
      nonRoutedTabRef.current = 'file';
      applyHomeTab('file');
      return;
    }

    if (nonRoutedTabRef.current) {
      applyHomeTab(nonRoutedTabRef.current);
      return;
    }

    if (!isAdminFetched || !selectedWorkspace) return;
    const landing = resolveWorkspaceHomeLanding({
      planId: selectedWorkspace.planId,
      isAdmin,
      isNcg: isNcgSeries(selectedWorkspace.seriesId),
    });
    const target = workspaceHomeLandingPath(routeWorkspaceId, landing);
    const targetPath = target.split('?')[0];
    if (location.pathname !== targetPath) {
      navigate(target, { replace: true });
      return;
    }
    applyHomeTab(landing === 'dashboard' ? 'dashboard' : landing === 'pages' ? 'page' : 'home');
  }, [
    applyHomeTab,
    isAdmin,
    isAdminFetched,
    location.pathname,
    location.search,
    navigate,
    routeWorkspaceId,
    selectedWorkspace,
  ]);

  useEffect(() => {
    const openFiles = () => {
      sessionStorage.removeItem('notreu.openTab');
      nonRoutedTabRef.current = 'file';
      applyHomeTab('file');
    };
    window.addEventListener('notreu-open-files', openFiles);
    return () => window.removeEventListener('notreu-open-files', openFiles);
  }, [applyHomeTab]);

  // 公開ボタン用のstate
  const [publishLoading, setPublishLoading] = useState(false);
  const [publicUrl, setPublicUrl] = useState<string>('');
  const [publicLinkEnabled, setPublicLinkEnabled] = useState(false);
  const [publicLinkReady, setPublicLinkReady] = useState(false);
  const [batchJobId, setBatchJobId] = useState<string | null>(null);
  
  // 公開URLをDBから永続的に取得するuseEffect
  useEffect(() => {
    // レースコンディション対策用のフラグ
    let isCancelled = false;
    
    // ワークスペース切り替え時に即座にURLをリセット（古いURLが表示されるのを防ぐ）
    setPublicUrl('');
    setPublicLinkEnabled(false);
    setPublicLinkReady(false);
    
    const checkPublicUrl = async () => {
      if (!routeWorkspaceId) {
        if (!isCancelled) setPublicLinkReady(true);
        return;
      }
      const routeWorkspace = workspaces.find((workspace) => workspace.id === routeWorkspaceId) || null;
      const currentPublishId = routeWorkspace?.publishId;
      
      if (!currentPublishId) {
        if (!isCancelled) setPublicLinkReady(true);
        return;
      }

      setPublicUrl(`/cg/${currentPublishId}`);

      try {
        const ref = doc(db as any, 'publicPageURL', currentPublishId);
        const snap = await getDoc(ref);
        
        // 非同期処理完了時にワークスペースが変わっている場合は処理をスキップ
        if (isCancelled) {
          return;
        }
        
        if (snap.exists()) {
          const data = snap.data() as { status?: unknown; published?: unknown };
          setPublicLinkEnabled(data.status === 'public' && data.published === true);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('公開URLの確認エラー:', error);
        }
      } finally {
        if (!isCancelled) setPublicLinkReady(true);
      }
    };

    checkPublicUrl();
    
    // クリーンアップ関数：次のuseEffect実行時に古い非同期処理の結果を無視
    return () => {
      isCancelled = true;
    };
  }, [routeWorkspaceId, workspaces]);

  const openDrsKeywordTab = () => {
    openWorkspaceTab('suggest');
    setDrsOpenSubTab('keyword');
    setDrsOpenRequestId((v) => v + 1);
    setMenuOpen(false);
  };
  const [batchProgress, setBatchProgress] = useState<{
    status: string;
    totalPages: number;
    processedPages: number;
    successCount: number;
    errorCount: number;
    progress: number;
  } | null>(null);

  // バッチ非公開処理の状態管理
  const [batchUnpublishJobId, setBatchUnpublishJobId] = useState<string | null>(null);
  const [batchUnpublishProgress, setBatchUnpublishProgress] = useState<{
    status: string;
    totalPages: number;
    processedPages: number;
    successCount: number;
    errorCount: number;
    progress: number;
  } | null>(null);

  // useEffect to check for existing jobs on workspace change
  useEffect(() => {
    if (routeWorkspaceId && !batchJobId) {
      checkExistingJobs();
    }
  }, [routeWorkspaceId, batchJobId]);

  // useEffect to check for existing unpublish jobs on workspace change
  useEffect(() => {
    if (routeWorkspaceId && !batchUnpublishJobId) {
      checkExistingUnpublishJobs();
    }
  }, [routeWorkspaceId, batchUnpublishJobId]);
  
  // 既存の進行中非公開ジョブをチェックする関数
  const checkExistingUnpublishJobs = async () => {
    if (!routeWorkspaceId) return;
    
    try {
      // Cloud Functions経由で既存ジョブをチェック
      const { checkExistingUnpublishJobs } = await import('../api/publicPages');
      const result = await checkExistingUnpublishJobs(routeWorkspaceId);

      if (result.found && result.jobId) {
        // ジョブIDを設定して進捗表示を開始
        setBatchUnpublishJobId(result.jobId);
        setIsUnpublishing(true);

        // 進捗データを設定
        setBatchUnpublishProgress({
          status: result.status || 'processing',
          totalPages: result.totalPages || 0,
          processedPages: result.processedPages || 0,
          successCount: result.successCount || 0,
          errorCount: result.errorCount || 0,
          progress: result.progress || 0
        });

        // 進捗をポーリング開始
        startUnpublishProgressPolling(result.jobId);

        return true; // 進行中のジョブが見つかった
      }

      return false;
    } catch (error) {
      console.error('❌ 既存非公開ジョブチェックエラー:', error);
      return false;
    }
  };

  // 既存の進行中ジョブをチェックする関数
  const checkExistingJobs = async () => {
    if (!routeWorkspaceId) return;
    
    try {
      // Cloud Functions経由で既存ジョブをチェック
      const { checkExistingBatchJobs } = await import('../api/publicPages');
      const result = await checkExistingBatchJobs(routeWorkspaceId);

      if (result.found && result.jobId) {
        // ジョブIDを設定して進捗表示を開始
        setBatchJobId(result.jobId);
        setPublishLoading(true);

        // 進捗データを設定
        setBatchProgress({
          status: result.status || 'processing',
          totalPages: result.totalPages || 0,
          processedPages: result.processedPages || 0,
          successCount: result.successCount || 0,
          errorCount: result.errorCount || 0,
          progress: result.progress || 0
        });

        // 進捗をポーリング開始
        startProgressPolling(result.jobId);

        return true; // 進行中のジョブが見つかった
      }

      return false;
    } catch (error) {
      console.error('❌ 既存ジョブチェックエラー:', error);
      return false;
    }
  };
  
  // 非公開処理の進捗ポーリングを開始する関数
  const startUnpublishProgressPolling = (jobId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const { getBatchUnpublishProgress } = await import('../api/publicPages');
        const progress = await getBatchUnpublishProgress(jobId);

        setBatchUnpublishProgress({
          status: progress.status,
          totalPages: progress.totalPages,
          processedPages: progress.processedPages,
          successCount: progress.successCount,
          errorCount: progress.errorCount,
          progress: progress.progress
        });

        // 処理完了 or 失敗したらポーリング終了
        if (progress.status === 'completed' || progress.status === 'failed') {
          clearInterval(pollInterval);
          
          if (progress.status === 'completed') {
            alert(uiText("Unpublish completed\nProcessed: {{successCount}} items\nErrors: {{errorCount}} items", { successCount: progress.successCount, errorCount: progress.errorCount }));
          } else {
            alert(uiText("Unpublish failed\nProcessed: {{successCount}} items\nErrors: {{errorCount}} items", { successCount: progress.successCount, errorCount: progress.errorCount }));
          }
          
          setIsUnpublishing(false);
          setBatchUnpublishJobId(null);
          setBatchUnpublishProgress(null);
          
          // 公開ページデータを再取得
          await fetchPublicPageData();
        }
      } catch (pollError) {
        console.error('❌ 非公開処理進捗取得エラー', pollError);
      }
    }, 2000);
  };

  // 進捗ポーリングを開始する関数
  const startProgressPolling = (jobId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const { getBatchPublishProgress } = await import('../api/publicPages');
        const progress = await getBatchPublishProgress(jobId);

        setBatchProgress({
          status: progress.status,
          totalPages: progress.totalPages,
          processedPages: progress.processedPages,
          successCount: progress.successCount,
          errorCount: progress.errorCount,
          progress: progress.progress
        });

        // 処理完了 or 失敗したらポーリング終了
        if (progress.status === 'completed' || progress.status === 'failed') {
          clearInterval(pollInterval);
          
          if (progress.status === 'completed') {
            let message = uiText("Public pages updated successfully\nProcessed: {{successCount}} items\nErrors: {{errorCount}} items\nembeddings: copying automatically on the backend", { successCount: progress.successCount, errorCount: progress.errorCount });
            
            // エラーがある場合は詳細を追加
            if (progress.errorCount > 0 && progress.errors && progress.errors.length > 0) {
              message += uiText("\n\n[Error details]");
              progress.errors.forEach((err, idx) => {
                message += uiText("\n{{v0}}. Page ID: {{pageId}}\n   Error: {{error}}", { v0: idx + 1, pageId: err.pageId, error: err.error });
              });
            }
            
            alert(message);
          } else {
            let message = uiText("Batch processing failed\nProcessed: {{successCount}} items\nErrors: {{errorCount}} items", { successCount: progress.successCount, errorCount: progress.errorCount });
            
            // エラー詳細を追加
            if (progress.errors && progress.errors.length > 0) {
              message += uiText("\n\n[Error details]");
              progress.errors.forEach((err, idx) => {
                message += uiText("\n{{v0}}. Page ID: {{pageId}}\n   Error: {{error}}", { v0: idx + 1, pageId: err.pageId, error: err.error });
              });
            }
            
            alert(message);
          }
          
          setPublishLoading(false);
          setBatchJobId(null);
        }
      } catch (pollError) {
        console.error('❌ 進捗取得エラー', pollError);
        // エラーが発生してもポーリングは継続
      }
    }, 2000); // 2秒ごとにポーリング

    // 30秒後にタイムアウト警告（処理は継続）
    setTimeout(() => {
      if (publishLoading) {}
    }, 30000);
  };
  
  // ワークスペースが選択された時に既存の進行中ジョブをチェック
  useEffect(() => {
    if (routeWorkspaceId && !batchJobId) {
      checkExistingJobs();
    }
  }, [routeWorkspaceId, batchJobId]);
  
  // 公開ページ概要用のstate
  const [totalPages, setTotalPages] = useState<number>(0);
  const [standbyPages, setStandbyPages] = useState<number>(0);
  const [publishedPages, setPublishedPages] = useState<number>(0);
  
  // DRS領域はコンポーネントに切り出し（Home.tsx肥大化防止）
  
  // 公開サイト設定用のstate（多言語設定を含む）
  const [siteSettings, setSiteSettings] = useState<{
    favicon?: string;
    tabTitle?: string;
    headerBackgroundColor?: string;
    headerHeight?: number;
    headerEnabled?: boolean;
    headerHasShadow?: boolean;
    headerIsFixed?: boolean;
    title?: string;
    titleColor?: string;
    mainColor?: string;
    linkTextColor?: string;
    titleFontSize?: number;
    logoUrl?: string;
    logoHeight?: number;
    logoLinkUrl?: string;
    logoOpenInNewTab?: boolean;
    linkText?: string;
    linkColor?: string;
    linkUrl?: string;
    linkOpenInNewTab?: boolean;
    linkBackgroundColor?: string;
    linkBorderColor?: string;
    linkPadding?: number;
    linkBorderRadius?: number;
    links?: Array<{
      id: string;
      text: string;
      color: string;
      url: string;
      openInNewTab: boolean;
      backgroundColor: string;
      borderColor: string;
      padding: number;
      borderRadius: number;
    }>;
    contentMaxWidth?: number;
    contentTitle?: string;
    contentTitleIcon?: import('../api/publicPages').ContentTitleIconSettings;
    contentSubtitle?: string;
    contentPreInputNotice?: string;
    contentPreInputNoticeRequireConsent?: boolean;
    contentPreInputNoticeConsentLabel?: string;
    contentPreInputNoticeConsentPlaceholder?: string;
    contentAlwaysInputNotice?: string;
    speechInputEnabled?: boolean;
    sessionHistoryEnabled?: boolean;
    contentPlaceholder?: string;
    contentDisclaimer?: string;
    keywordsLabel?: string;
    // 公開AI検索ページのテキストサイズ切替
    textSizeControlEnabled?: boolean;
    textSizeDefault?: 'small' | 'medium' | 'large' | 'xlarge';
    textSizeLabels?: {
      small: string;
      medium: string;
      large: string;
      xlarge: string;
    };
    mainTextColor?: string;
    subTextColor?: string;
    keywordTextColor?: string;
    footerBackgroundColor?: string;
    footerHasBorderTop?: boolean;
    footerBorderTopColor?: string;
    footerText?: string;
    footerTextColor?: string;
    // 多言語設定
    isMultilingual?: boolean;
    defaultLanguage?: string;
    availableLanguages?: string[];
    textFont?: string;
    textFontEn?: string;
    textFontJp?: string;
    updatedAt?: any;
  }>({});
  const [siteSettingsLoading, setSiteSettingsLoading] = useState(false);
  const [ogTitle, setOgTitle] = useState<string>('');
  
  // カレンダー関連の状態
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [deleteUntitledModalOpen, setDeleteUntitledModalOpen] = useState(false);
  const [backupModalOpen, setBackupModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [folderMoveModalOpen, setFolderMoveModalOpen] = useState(false);
  const [isMovingToFolder, setIsMovingToFolder] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const { selectedHashtag, setSelectedHashtag } = useHashtagContext();
  const { searchQuery, setSearchQuery, isSearching, setIsSearching } = useSearchContext();
  const { refreshHashtags } = useHashtagContext();
  const { refreshTrigger, refreshBoardList } = useBoardListContext();
  const { isArchiveMode, setIsArchiveMode } = useArchiveContext();

  // レスポンシブ対応用の状態
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);

  // レスポンシブ対応のウィンドウサイズ監視
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsMobileOrTablet(window.innerWidth <= 1024);
    };
    
    // 初期チェック
    checkIsMobile();
    
    // リサイズイベントリスナーを追加
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // カレンダータブが開かれたときに今日を初期選択
  useEffect(() => {
    if (isCalendarMode && !selectedDate) {
      const today = new Date();
      // 時分秒を0にして純粋な日付比較を安定化
      today.setHours(0, 0, 0, 0);
      setSelectedDate(today);
    }
  }, [isCalendarMode, selectedDate]);

  // 公開モードになったときに設定を読み込み
  useEffect(() => {
    if (isPublicMode && routeWorkspaceId) {
      loadSiteSettings();
    }
  }, [isPublicMode, routeWorkspaceId]);

  // カレンダーデータを BoardList の currentBoards から派生（独自の Firestore クエリを廃止）
  const calendarData = useMemo(() => {
    if (currentBoards.length === 0) return { dateData: [] as DateData[], boards: currentBoards };

    const parseDateFromStructured = (previewData: any): DateData | null => {
      try {
        const sd = previewData?.structuredData;
        const iso: string | undefined = sd?.datePublished || sd?.eventDate;
        if (!iso || typeof iso !== 'string') return null;
        let d: Date;
        if (iso.includes('T') || iso.includes('Z')) {
          d = new Date(iso);
        } else if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
          d = new Date(iso + 'T00:00:00');
        } else {
          d = new Date(iso);
        }
        if (isNaN(d.getTime())) return null;
        return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
      } catch (_) {
        return null;
      }
    };

    const extractedDates: DateData[] = [];
    currentBoards.forEach((board) => {
      const searchData = board.searchData as any;
      if (searchData?.DateOnPage) {
        const dateInfo = searchData.DateOnPage;
        extractedDates.push({ year: dateInfo.year, month: dateInfo.month, day: dateInfo.day });
      } else {
        const fallback = parseDateFromStructured((board as any).previewData);
        if (fallback) {
          extractedDates.push(fallback);
        }
      }
    });

    return { dateData: extractedDates, boards: currentBoards };
  }, [currentBoards]);

  const calendarLoading = currentBoards.length === 0 && loading;
  const calendarError = null;
  const { dateData, boards } = calendarData;

  // メニューの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (menuOpen && !target.closest('.menu-container')) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // 未ログインの場合はログイン画面にリダイレクト（ローディング中は除外）
  useEffect(() => {
    if (!loading && !user) {
      navigate('/sys/login', { replace: true });
    }
  }, [user, loading, navigate]);

  // ワークスペースがURLにない場合は先頭ワークスペースに遷移
  useEffect(() => {
    if (!loading && workspaces.length > 0 && !routeWorkspaceId) {
      navigate(`/${workspaces[0].id}`, { replace: true });
      setTimeout(() => {
        refreshBoardList();
      }, 100);
    }
  }, [loading, workspaces, routeWorkspaceId, navigate, refreshBoardList]);

  // ワークスペース一覧が更新されたときにボード一覧も更新
  // 注意: BoardListは既にrouteWorkspaceIdの変更を検知して自動更新するため、
  // ここでのrefreshBoardList()呼び出しは重複読み込みの原因となる
  // useEffect(() => {
  //   console.log('🏠 Home useEffect[ワークスペース一覧更新]:', { 
  //     workspacesLength: workspaces.length, 
  //     routeWorkspaceId,
  //     condition: workspaces.length > 0 && routeWorkspaceId
  //   });
  //   if (workspaces.length > 0 && routeWorkspaceId) {
  //     console.log('🏠 Home: ワークスペース一覧更新 - refreshBoardList()実行');
  //     refreshBoardList();
  //   }
  // }, [workspaces, routeWorkspaceId, refreshBoardList]);

  // 公開モードタブが開かれた時とワークスペースが変更された時に公開ページデータを取得
  useEffect(() => {
    if (isPublicMode && routeWorkspaceId) {
      fetchPublicPageData();
    }
  }, [isPublicMode, routeWorkspaceId]);

  const handleSelectionModeToggle = () => {
    setIsSelectionMode(!isSelectionMode);
    if (isSelectionMode) {
      setSelectedBoardIds([]);
    }
  };

  // 全て選択/全解除の処理
  const handleSelectAll = () => {
    if (!routeWorkspaceId || currentBoards.length === 0) return;
    
    const allBoardIds = currentBoards.map(board => board.id);
    
    if (selectedBoardIds.length === allBoardIds.length) {
      // 全て選択されている場合は全解除
      setSelectedBoardIds([]);
    } else {
      // 全て選択
      setSelectedBoardIds(allBoardIds);
    }
  };

  // 空のMemoページ削除処理
  const handleDeleteUntitledPages = async () => {
    if (!routeWorkspaceId || isDeleting) return;
    
    try {
      setIsDeleting(true);

      const result = await deleteNoContentMemos(routeWorkspaceId);

      if (result.success) {
        alert(uiText("Deleted {{deletedCount}} empty Memo pages.", { deletedCount: result.deletedCount }));

        // ボード一覧を更新
        refreshBoardList();
      } else {
        console.error('❌ Home: 空のMemoページ削除失敗:', result.error);
        alert(uiText("Failed to delete: ") + result.error);
      }
    } catch (error) {
      console.error('❌ Home: 空のMemoページ削除でエラー:', error);
      alert(uiText("An error occurred while deleting."));
    } finally {
      setIsDeleting(false);
      setDeleteUntitledModalOpen(false);
    }
  };

  // フォルダー移動処理
  const handleMoveToFolder = async (targetFolderId: string | null) => {
    if (!routeWorkspaceId || selectedBoardIds.length === 0 || isMovingToFolder) return;

    try {
      setIsMovingToFolder(true);

      const result = await movePagesToFolder({
        workspaceId: routeWorkspaceId,
        pageIds: selectedBoardIds,
        targetFolderId,
        folders
      });

      if (result.success) {
        const folderName = getFolderName(targetFolderId, folders);
        alert(uiText("Moved {{length}} pages to \"{{folderName}}\".", { length: selectedBoardIds.length, folderName: folderName }));

        // 選択状態をリセット
        setSelectedBoardIds([]);
        setIsSelectionMode(false);

        // ボード一覧を更新
        refreshBoardList();
        void queryClient.invalidateQueries({ queryKey: ['page-sidebar-folder-counts', routeWorkspaceId] });
      } else {
        console.error('❌ Home: フォルダー移動失敗:', result.error);
        alert(uiText("Failed to move folder: ") + result.error);
      }
    } catch (error) {
      console.error('❌ Home: フォルダー移動でエラー:', error);
      alert(uiText("An error occurred while moving the folder."));
    } finally {
      setIsMovingToFolder(false);
      setFolderMoveModalOpen(false);
    }
  };

  const handleArchiveBoards = async () => {
    if (!routeWorkspaceId || selectedBoardIds.length === 0) return;

    try {
      const updatePromises = selectedBoardIds.map(async (boardId) => {
        const boardRef = doc(db, `workspaces/${routeWorkspaceId}/boards/${boardId}`);
        
        // ボードをアーカイブ
        await updateDoc(boardRef, { archived: true });
        
        // ハッシュタグの管理
        try {
          await handleArchivedBoardHashtags(routeWorkspaceId, boardId, true);
        } catch (hashtagError) {
          console.error('ハッシュタグ管理エラー:', hashtagError);
          // ハッシュタグ管理でエラーが発生しても、アーカイブ処理は続行
        }
      });

      await Promise.all(updatePromises);

      // 選択状態をリセット
      setSelectedBoardIds([]);
      setIsSelectionMode(false);
      setShowArchiveConfirm(false);

      // ボード一覧を再取得
      refreshBoardList();

      // ハッシュタグ一覧を更新
      refreshHashtags();
    } catch (error) {
      console.error('アーカイブエラー:', error);
    }
  };

  /**
   * 知識軸を ON にする（公開RAGの回答候補に入れる）。
   * silent のときは完了通知を出さない（ページ軸の変更と続けて実行する場合）。
   */
  const handlePublishSelectedPages = async (pageIds: string[], options?: { silent?: boolean }) => {
    if (!routeWorkspaceId || pageIds.length === 0) {
      return;
    }

    const pageCount = pageIds.length; // クロージャで値を保持

    setIsPublishing(true);
    setPublishProgress(0);
    setPublishingPageCount(pageCount);

    try {
      // 進捗: 公開処理開始
      setPublishProgress(10);

      const publishSelectedPagesFn = httpsCallable(functions, 'publishSelectedPagesFn');
      const result = await publishSelectedPagesFn({
        workspaceId: routeWorkspaceId,
        pageIds
      });

      // この時点で embeddings のコピーまで完了しており、公開RAGは回答に使える
      setPublishProgress(80);

      const emptyPageCount = Array.isArray((result.data as any)?.pageIdsWithoutContent)
        ? (result.data as any).pageIdsWithoutContent.length
        : 0;

      // 検索サジェスト生成は Cloud Run Job のコールドスタートで数分かかることがある。
      // 回答に使えるかどうかとは独立なので、待たずに投げっぱなしにする。
      void requestDeepReasoningSuggestionsWithBatching(routeWorkspaceId, [...pageIds])
        .then((drResult) => {
          console.log('✅ [FRONT] 検索サジェスト生成完了', drResult);
        })
        .catch((drError) => {
          console.error('❌ [FRONT] 検索サジェスト生成に失敗（公開状態には影響しません）', drError);
        });

      setSelectedBoardIds([]);

      // 公開ページデータを再取得（タイムアウトエラーを防ぐため、エラーハンドリングを追加）
      try {
        console.log('📊 公開ページデータ再取得開始');
        await fetchPublicPageData();
        console.log('✅ 公開ページデータ再取得完了');
      } catch (fetchError) {
        console.error('⚠️ 公開ページデータ再取得エラー（処理は継続）:', fetchError);
        // データ取得に失敗しても公開処理自体は成功しているので継続
      }

      // 進捗: 最終処理完了
      setPublishProgress(100);

      // すべての処理が完了してから成功メッセージを表示
      setTimeout(() => {
        if (!options?.silent) {
          const baseMessage = uiText("Set {{pageCount}} notes to be used in AI answers", { pageCount: pageCount });
          alert(
            emptyPageCount > 0
              ? uiText("{{baseMessage}}\n\n{{emptyPageCount}} items have empty bodies, so AI cannot use these notes. Write the body and they will be included in answers automatically.", { baseMessage: baseMessage, emptyPageCount: emptyPageCount })
              : baseMessage
          );
        }
        setIsPublishing(false);
        setPublishProgress(0);
        setPublishingPageCount(0);
      }, 500);
    } catch (error: any) {
      console.error('❌ 公開処理エラー:', error);
      
      let errorMessage = uiText("An error occurred while publishing");
      if (error?.code === 'functions/permission-denied') {
        errorMessage = uiText("Insufficient permissions. Only the workspace owner or an admin can publish.");
      } else if (error?.message) {
        errorMessage = uiText("Error: {{message}}", { message: error.message });
      }
      
      alert(errorMessage);
      setIsPublishing(false);
      setPublishProgress(0);
      setPublishingPageCount(0);
    }
  };

  /** 知識軸を OFF にする。Webページも併せて取り下げられる（バッチ処理版）。 */
  const handleUnpublishSelectedPages = async (pageIds: string[]) => {
    if (!routeWorkspaceId || pageIds.length === 0) return;

    const pageCount = pageIds.length;

    setIsUnpublishing(true);
    setBatchUnpublishProgress(null);
    setUnpublishProgress(0);
    setUnpublishingPageCount(pageCount);

    try {
      const { startBatchUnpublishContent } = await import('../api/publicPages');
      const batchRes = await startBatchUnpublishContent(routeWorkspaceId, pageIds);

      if (batchRes.status === 'already_running') {
        alert(uiText("Unpublishing is already running"));
        setIsUnpublishing(false);
        return;
      }

      if (!batchRes.jobId) {
        throw new Error(uiText("Could not get the job ID"));
      }

      setBatchUnpublishJobId(batchRes.jobId);
      setSelectedBoardIds([]);

      // 進捗をポーリング開始
      startUnpublishProgressPolling(batchRes.jobId);
    } catch (error: any) {
      console.error('❌ バッチ非公開処理エラー:', error);
      let errorMessage = uiText("An error occurred while unpublishing");
      if (error?.code === 'functions/permission-denied') {
        errorMessage = uiText("Insufficient permissions. Only the workspace owner or an admin can unpublish.");
      } else if (error?.message) {
        errorMessage = uiText("Error: {{message}}", { message: error.message });
      }
      alert(errorMessage);
      setIsUnpublishing(false);
      setBatchUnpublishJobId(null);
      setUnpublishProgress(0);
      setUnpublishingPageCount(0);
    }
  };

  // ワークスペースのマスタースイッチ（ページをWeb公開する）。ページ軸はこれとの AND
  const isNcgSeriesWorkspace = isNcgSeries(selectedWorkspace?.seriesId);
  const { can } = useWorkspaceEntitlements();
  useEffect(() => {
    if (!routeWorkspaceId || !isNcgSeriesWorkspace) {
      setHtmlPagesEnabledForWs(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { getHtmlPagesEnabled } = await import('../api/publicPages');
        const enabled = await getHtmlPagesEnabled(routeWorkspaceId);
        if (!cancelled) setHtmlPagesEnabledForWs(enabled);
      } catch (error) {
        console.error('⚠️ ページWeb公開設定の取得に失敗:', error);
        if (!cancelled) setHtmlPagesEnabledForWs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [routeWorkspaceId, isNcgSeriesWorkspace]);

  // 選択中ノートの 2 軸の状態。混在しているときは 'mixed'
  const selectedPublishAxes = useMemo(() => {
    const selected = currentBoards.filter(board => selectedBoardIds.includes(board.id));
    const summarize = (values: boolean[]): 'on' | 'off' | 'mixed' => {
      if (values.length === 0) return 'off';
      if (values.every(Boolean)) return 'on';
      if (values.every(v => !v)) return 'off';
      return 'mixed';
    };
    return {
      count: selected.length,
      rag: summarize(selected.map(b => b.publicRagEnabled === true)),
      // ワークスペースのマスタースイッチが OFF のあいだはページ軸を出さない（設定自体は保持）
      web: htmlPagesEnabledForWs === false
        ? 'off' as const
        : summarize(selected.map(b => b.publicWebIntended === true)),
      ragOffIds: selected.filter(b => b.publicRagEnabled !== true).map(b => b.id),
    };
  }, [currentBoards, selectedBoardIds, htmlPagesEnabledForWs]);

  /**
   * ページ軸（Webページとして公開する）を切り替える。
   * 知識軸は落とさないので、OFF にしても公開AIは答え続ける。
   */
  const handleSetPagesWebEnabled = async (
    enabled: boolean,
    pageIds: string[],
    ragOffIds: string[]
  ) => {
    if (!routeWorkspaceId || pageIds.length === 0) return;

    setIsSettingWebEnabled(true);

    try {
      const { setPagesWebEnabled } = await import('../api/publicPages');

      // ページ軸 ON は知識軸 ON を含意する。先に知識軸を立てて DRS も移しておく
      if (enabled && ragOffIds.length > 0) {
        const publishSelectedPagesFn = httpsCallable(functions, 'publishSelectedPagesFn');
        await publishSelectedPagesFn({ workspaceId: routeWorkspaceId, pageIds: ragOffIds });
      }

      const res = await setPagesWebEnabled(routeWorkspaceId, pageIds, enabled);

      setSelectedBoardIds([]);
      await fetchPublicPageData().catch(fetchError => {
        console.error('⚠️ 公開ページデータ再取得エラー（処理は継続）:', fetchError);
      });

      if (enabled) {
        if (res.publishJob?.status === 'started' && res.publishJob.jobId) {
          setBatchJobId(res.publishJob.jobId);
          startProgressPolling(res.publishJob.jobId);
        } else if (res.publishJob?.status === 'already_running') {
          alert(uiText("Web page generation is already running. Please try again after it finishes."));
        } else {
          alert(uiText("Set {{updatedCount}} notes to be published as web pages", { updatedCount: res.updatedCount }));
        }
      } else {
        alert(uiText("Unpublished web pages for {{updatedCount}} notes (still used in AI answers)", { updatedCount: res.updatedCount }));
      }
    } catch (error: any) {
      console.error('❌ Webページ公開設定エラー:', error);
      let errorMessage = uiText("Failed to update web page publishing settings");
      if (error?.code === 'functions/failed-precondition') {
        errorMessage = error?.message || uiText("Turn on \"Publish page on the web\" in publish settings");
      } else if (error?.code === 'functions/permission-denied') {
        errorMessage = uiText("Insufficient permissions. Only the workspace owner or an admin can change settings.");
      } else if (error?.message) {
        errorMessage = uiText("Error: {{message}}", { message: error.message });
      }
      alert(errorMessage);
    } finally {
      setIsSettingWebEnabled(false);
    }
  };

  /**
   * 公開設定メニューの「確定」で、2 軸の変更をまとめて反映する。
   * 変わった軸だけを触るので、混在した選択でも意図しない側は現状維持になる。
   */
  const handleApplyPublishAxes = async (next: { rag: AxisState; web: AxisState }) => {
    if (!routeWorkspaceId || selectedBoardIds.length === 0) return;
    if (!can('publicRag')) return;

    const pageIds = [...selectedBoardIds];
    const ragOffIds = [...selectedPublishAxes.ragOffIds];
    const ragChanged = next.rag !== selectedPublishAxes.rag;
    const webChanged = can('publicRag.webPages') && next.web !== selectedPublishAxes.web;

    setShowPublishMenu(false);
    if (!ragChanged && !webChanged) return;

    // 知識軸 OFF はWebページも一緒に取り下げるので、これ 1 本で足りる
    if (ragChanged && next.rag === 'off') {
      await handleUnpublishSelectedPages(pageIds);
      return;
    }

    // ページ軸 ON は知識軸 ON を含意し、内部で先に知識軸を立てる
    if (webChanged && next.web === 'on') {
      await handleSetPagesWebEnabled(true, pageIds, ragOffIds);
      return;
    }

    // 残りは「知識軸 ON」「ページ軸 OFF」の組み合わせ。両方あるときは通知を 1 回にまとめる
    if (ragChanged && next.rag === 'on') {
      await handlePublishSelectedPages(pageIds, { silent: webChanged });
    }
    if (webChanged && next.web === 'off') {
      await handleSetPagesWebEnabled(false, pageIds, ragOffIds);
    }
  };

  // 公開ページデータを取得する関数
  const fetchPublicPageData = async () => {
    if (!routeWorkspaceId) return;
    
    try {
      console.log('📊 fetchPublicPageData 開始: workspaceId=', routeWorkspaceId);
      
      // /publicPages/ワークスペースID/pages のコレクションから全ドキュメントを取得
      const pagesRef = collection(db, 'publicPages', routeWorkspaceId, 'pages');
      
      console.log('📊 getDocs 実行中...');
      const startTime = Date.now();
      const snapshot = await getDocs(pagesRef);
      const elapsed = Date.now() - startTime;
      console.log(`✅ getDocs 完了: ${snapshot.size}件取得 (${elapsed}ms)`);

      let totalCount = 0;
      let standbyCount = 0;
      let publishedCount = 0;

      snapshot.forEach((doc) => {
        const data = doc.data();
        totalCount++;
        
        // status が "public" の場合のみカウント
        if (data.status === 'public') {
          // published が true の場合は公開数
          if (data.published === true) {
            publishedCount++;
          } else {
            // published が false または null または項目なしの場合は公開スタンバイ数
            standbyCount++;
          }
        }
      });

      // stateを更新
      setTotalPages(totalCount);
      setStandbyPages(standbyCount);
      setPublishedPages(publishedCount);
      
      console.log(`📊 集計結果: total=${totalCount}, standby=${standbyCount}, published=${publishedCount}`);
    } catch (error: any) {
      console.error('❌ 公開ページデータ取得エラー:', error);
      console.error('❌ エラー詳細:', {
        code: error?.code,
        message: error?.message,
        name: error?.name
      });
      // エラーを再スローして呼び出し元でキャッチできるようにする
      throw error;
    }
  };

  // 公開サイト設定の読み込み
  const loadSiteSettings = async () => {
    if (!routeWorkspaceId) return;
    
    try {
      setSiteSettingsLoading(true);
      const { getPublicSiteSettings } = await import('../api/publicPages');
      const settings = await getPublicSiteSettings(routeWorkspaceId);
      if (settings) {
        setSiteSettings({
          favicon: settings.favicon || '',
          tabTitle: settings.tabTitle || '',
          headerBackgroundColor: settings.headerBackgroundColor || '#ffffff',
          headerHeight: settings.headerHeight || 60,
          headerEnabled: settings.headerEnabled ?? false,
          headerHasShadow: settings.headerHasShadow ?? true,
          headerIsFixed: settings.headerIsFixed ?? false,
          title: settings.title || '',
          titleColor: settings.titleColor || '#333333',
          titleFontSize: settings.titleFontSize || 24,
          logoUrl: settings.logoUrl || '',
          logoHeight: settings.logoHeight || 40,
          logoLinkUrl: settings.logoLinkUrl || '',
          logoOpenInNewTab: settings.logoOpenInNewTab ?? false,
          linkText: settings.linkText || '',
          linkColor: settings.linkColor || '#333333',
          linkUrl: settings.linkUrl || '',
          linkOpenInNewTab: settings.linkOpenInNewTab ?? false,
          linkBackgroundColor: settings.linkBackgroundColor || 'transparent',
          linkBorderColor: settings.linkBorderColor || 'transparent',
          linkPadding: settings.linkPadding || 8,
          linkBorderRadius: settings.linkBorderRadius || 0,
          links: settings.links || [],
          contentMaxWidth: settings.contentMaxWidth || 1200,
          contentTitle: settings.contentTitle || '',
          contentTitleIcon: settings.contentTitleIcon,
          contentSubtitle: settings.contentSubtitle || '',
          contentPreInputNotice: settings.contentPreInputNotice || '',
          contentPreInputNoticeRequireConsent: settings.contentPreInputNoticeRequireConsent ?? false,
          contentPreInputNoticeConsentLabel: settings.contentPreInputNoticeConsentLabel || '',
          contentPreInputNoticeConsentPlaceholder: settings.contentPreInputNoticeConsentPlaceholder || '',
          contentAlwaysInputNotice: settings.contentAlwaysInputNotice || '',
          speechInputEnabled: settings.speechInputEnabled ?? false,
          sessionHistoryEnabled: settings.sessionHistoryEnabled ?? false,
          contentPlaceholder: settings.contentPlaceholder || '',
          contentDisclaimer: settings.contentDisclaimer || '',
          keywordsLabel: settings.keywordsLabel || '',
          mainColor: settings.mainColor || '',
          linkTextColor: settings.linkTextColor || '',
          // テキストサイズ切替（公開AI検索ページ）
          textSizeControlEnabled: settings.textSizeControlEnabled ?? false,
          textSizeDefault: settings.textSizeDefault || 'medium',
          textSizeLabels: settings.textSizeLabels,
          mainTextColor: settings.mainTextColor || '',
          subTextColor: settings.subTextColor || '',
          keywordTextColor: settings.keywordTextColor || '',
          footerBackgroundColor: settings.footerBackgroundColor || '#f8f9fa',
          footerHasBorderTop: settings.footerHasBorderTop ?? true,
          footerBorderTopColor: settings.footerBorderTopColor || '#e9ecef',
          footerText: settings.footerText || '',
          footerTextColor: settings.footerTextColor || '#6c757d',
          // 多言語設定を反映
          isMultilingual: settings.isMultilingual ?? false,
          defaultLanguage: settings.defaultLanguage || undefined,
          availableLanguages: settings.availableLanguages || [],
          textFont: settings.textFont || 'default',
          textFontEn: settings.textFontEn ?? settings.textFont ?? 'default',
          textFontJp: settings.textFontJp ?? settings.textFont ?? 'default',
          updatedAt: settings.updatedAt
        });
      }
      
      // OGタイトルを取得
      if (selectedWorkspace?.publishId) {
        try {
          const publishRef = doc(db as any, 'publicPageURL', selectedWorkspace.publishId);
          const publishSnap = await getDoc(publishRef);
          if (publishSnap.exists()) {
            const publishData = publishSnap.data() as any;
            setOgTitle(publishData.og?.title || '');
          }
        } catch (ogError) {
          console.error('❌ OGタイトル読み込みエラー:', ogError);
        }
      }
    } catch (error) {
      console.error('❌ サイト設定読み込みエラー:', error);
    } finally {
      setSiteSettingsLoading(false);
    }
  };

  // ファビコン設定の保存
  const handleFaviconChange = async (faviconUrl: string) => {
    if (!routeWorkspaceId) return;
    
    try {
      const { savePublicSiteSettings } = await import('../api/publicPages');
      await savePublicSiteSettings(routeWorkspaceId, { favicon: faviconUrl });
      setSiteSettings(prev => ({ ...prev, favicon: faviconUrl }));
    } catch (error) {
      console.error('❌ ファビコン設定保存エラー:', error);
      alert(uiText("Failed to save favicon settings."));
    }
  };

  // タブタイトル設定の保存
  const handleTabTitleChange = async (tabTitle: string) => {
    if (!routeWorkspaceId) return;

    try {
      const { savePublicSiteSettings } = await import('../api/publicPages');
      await savePublicSiteSettings(routeWorkspaceId, { tabTitle });
      setSiteSettings(prev => ({ ...prev, tabTitle }));
    } catch (error) {
      console.error('❌ タブタイトル設定保存エラー:', error);
      alert(uiText("Failed to save tab title settings."));
    }
  };

  // コンテンツ設定の保存
  const handleContentSettingsChange = async (contentSettings: {
    contentTitle: string;
    contentTitleIcon?: import('../api/publicPages').ContentTitleIconSettings;
    contentSubtitle: string;
    contentPreInputNotice?: string;
    contentPreInputNoticeAlign?: 'left' | 'center' | 'right';
    contentPreInputNoticePaddingX?: number;
    contentPreInputNoticeRequireConsent?: boolean;
    contentPreInputNoticeConsentLabel?: string;
    contentPreInputNoticeConsentPlaceholder?: string;
    contentAlwaysInputNotice?: string;
    speechInputEnabled?: boolean;
    sessionHistoryEnabled?: boolean;
    contentPlaceholder: string;
    contentDisclaimer?: string;
    keywordsLabel?: string;
    mainColor?: string;
    linkTextColor?: string;
    // 公開AI検索ページのテキストサイズ切替
    textSizeControlEnabled?: boolean;
    textSizeDefault?: 'small' | 'medium' | 'large' | 'xlarge';
    textSizeLabels?: {
      small: string;
      medium: string;
      large: string;
      xlarge: string;
    };
    mainTextColor?: string;
    subTextColor?: string;
    keywordTextColor?: string;
  }) => {
    if (!routeWorkspaceId) {
      throw new Error(uiText("Workspace ID is not set"));
    }

    try {
      console.log('💾 コンテンツ設定を保存します', {
        workspaceId: routeWorkspaceId,
        path: `publicPages/${routeWorkspaceId}/settings/site`,
        contentTitle: contentSettings.contentTitle,
        contentSubtitle: contentSettings.contentSubtitle,
        contentPlaceholder: contentSettings.contentPlaceholder
      });

      const { savePublicSiteSettings } = await import('../api/publicPages');
      await savePublicSiteSettings(routeWorkspaceId, contentSettings);
      
      setSiteSettings(prev => ({ 
        ...prev, 
        contentTitle: contentSettings.contentTitle,
        contentTitleIcon: contentSettings.contentTitleIcon,
        contentSubtitle: contentSettings.contentSubtitle,
        contentPreInputNotice: contentSettings.contentPreInputNotice,
        contentPreInputNoticeRequireConsent: contentSettings.contentPreInputNoticeRequireConsent,
        contentPreInputNoticeConsentLabel: contentSettings.contentPreInputNoticeConsentLabel,
        contentPreInputNoticeConsentPlaceholder: contentSettings.contentPreInputNoticeConsentPlaceholder,
        contentAlwaysInputNotice: contentSettings.contentAlwaysInputNotice,
        speechInputEnabled: contentSettings.speechInputEnabled,
        sessionHistoryEnabled: contentSettings.sessionHistoryEnabled,
        contentPlaceholder: contentSettings.contentPlaceholder,
        contentDisclaimer: contentSettings.contentDisclaimer,
        keywordsLabel: contentSettings.keywordsLabel,
        mainColor: contentSettings.mainColor,
        linkTextColor: contentSettings.linkTextColor,
        textSizeControlEnabled: contentSettings.textSizeControlEnabled,
        textSizeDefault: contentSettings.textSizeDefault,
        textSizeLabels: contentSettings.textSizeLabels,
        mainTextColor: contentSettings.mainTextColor,
        subTextColor: contentSettings.subTextColor,
        keywordTextColor: contentSettings.keywordTextColor
      }));

      console.log('✅ コンテンツ設定の保存が完了しました');
    } catch (error) {
      console.error('❌ コンテンツ設定保存エラー:', error);
      throw error; // エラーを上位に伝播
    }
  };

  // フッター設定の保存
  const handleFooterSettingsChange = async (footerSettings: {
    footerBackgroundColor: string;
    footerHasBorderTop: boolean;
    footerBorderTopColor: string;
    footerText: string;
    footerTextColor: string;
  }) => {
    if (!routeWorkspaceId) {
      throw new Error(uiText("Workspace ID is not set"));
    }

    try {
      console.log('💾 フッター設定を保存します', {
        workspaceId: routeWorkspaceId,
        path: `publicPages/${routeWorkspaceId}/settings/site`,
        footerBackgroundColor: footerSettings.footerBackgroundColor,
        footerHasBorderTop: footerSettings.footerHasBorderTop,
        footerBorderTopColor: footerSettings.footerBorderTopColor,
        footerText: footerSettings.footerText,
        footerTextColor: footerSettings.footerTextColor
      });

      const { savePublicSiteSettings } = await import('../api/publicPages');
      await savePublicSiteSettings(routeWorkspaceId, footerSettings);
      
      setSiteSettings(prev => ({ 
        ...prev, 
        footerBackgroundColor: footerSettings.footerBackgroundColor,
        footerHasBorderTop: footerSettings.footerHasBorderTop,
        footerBorderTopColor: footerSettings.footerBorderTopColor,
        footerText: footerSettings.footerText,
        footerTextColor: footerSettings.footerTextColor
      }));

      console.log('✅ フッター設定の保存が完了しました');
    } catch (error) {
      console.error('❌ フッター設定保存エラー:', error);
      throw error; // エラーを上位に伝播
    }
  };

  // ヘッダー設定の保存
  const handleHeaderSettingsChange = async (headerSettings: {
    enabled?: boolean;
    backgroundColor?: string;
    height?: number;
    hasShadow?: boolean;
    isFixed?: boolean;
    title?: string;
    titleColor?: string;
    titleFontSize?: number;
    logoUrl?: string;
    logoHeight?: number;
    logoLinkUrl?: string;
    logoOpenInNewTab?: boolean;
    linkText?: string;
    linkColor?: string;
    linkUrl?: string;
    linkOpenInNewTab?: boolean;
    linkBackgroundColor?: string;
    linkBorderColor?: string;
    linkPadding?: number;
    linkBorderRadius?: number;
    links?: Array<{
      id: string;
      text: string;
      color: string;
      url: string;
      openInNewTab: boolean;
      backgroundColor: string;
      borderColor: string;
      padding: number;
      borderRadius: number;
    }>;
    contentMaxWidth?: number;
  }) => {
    if (!routeWorkspaceId) return;
    
    try {
      const settingsToSave = {
        headerBackgroundColor: headerSettings.backgroundColor,
        headerHeight: headerSettings.height,
        headerEnabled: headerSettings.enabled,
        headerHasShadow: headerSettings.hasShadow,
        headerIsFixed: headerSettings.isFixed,
        title: headerSettings.title,
        titleColor: headerSettings.titleColor,
        titleFontSize: headerSettings.titleFontSize,
        logoUrl: headerSettings.logoUrl,
        logoHeight: headerSettings.logoHeight,
        logoLinkUrl: headerSettings.logoLinkUrl,
        logoOpenInNewTab: headerSettings.logoOpenInNewTab,
        linkText: headerSettings.linkText,
        linkColor: headerSettings.linkColor,
        linkUrl: headerSettings.linkUrl,
        linkOpenInNewTab: headerSettings.linkOpenInNewTab,
        linkBackgroundColor: headerSettings.linkBackgroundColor,
        linkBorderColor: headerSettings.linkBorderColor,
        linkPadding: headerSettings.linkPadding,
        linkBorderRadius: headerSettings.linkBorderRadius,
        links: headerSettings.links,
        contentMaxWidth: headerSettings.contentMaxWidth
      };
      
      // undefinedの値を除外（Firestoreはundefinedを保存できない）
      const cleanedSettings = Object.fromEntries(
        Object.entries(settingsToSave).filter(([_, value]) => value !== undefined)
      );
      
      console.log('💾 Home: ヘッダー設定を保存します', {
        workspaceId: routeWorkspaceId,
        logoUrl: headerSettings.logoUrl,
        hasLogoUrl: !!headerSettings.logoUrl,
        title: headerSettings.title,
        hasTitle: !!headerSettings.title,
        cleanedSettings
      });
      
      const { savePublicSiteSettings } = await import('../api/publicPages');
      await savePublicSiteSettings(routeWorkspaceId, cleanedSettings);
      setSiteSettings(prev => ({
        ...prev,
        headerBackgroundColor: headerSettings.backgroundColor,
        headerHeight: headerSettings.height,
        headerEnabled: headerSettings.enabled,
        headerHasShadow: headerSettings.hasShadow,
        headerIsFixed: headerSettings.isFixed,
        title: headerSettings.title,
        titleColor: headerSettings.titleColor,
        titleFontSize: headerSettings.titleFontSize,
        logoUrl: headerSettings.logoUrl,
        logoHeight: headerSettings.logoHeight,
        logoLinkUrl: headerSettings.logoLinkUrl,
        logoOpenInNewTab: headerSettings.logoOpenInNewTab,
        linkText: headerSettings.linkText,
        linkColor: headerSettings.linkColor,
        linkUrl: headerSettings.linkUrl,
        linkOpenInNewTab: headerSettings.linkOpenInNewTab,
        linkBackgroundColor: headerSettings.linkBackgroundColor,
        linkBorderColor: headerSettings.linkBorderColor,
        linkPadding: headerSettings.linkPadding,
        linkBorderRadius: headerSettings.linkBorderRadius,
        links: headerSettings.links,
        contentMaxWidth: headerSettings.contentMaxWidth
      }));
    } catch (error) {
      console.error('❌ ヘッダー設定保存エラー:', error);
      alert(uiText("Failed to save header settings."));
    }
  };

  const handleGeneralSettingsChange = async (generalSettings: { textFontEn?: string; textFontJp?: string }) => {
    if (!routeWorkspaceId) return;
    try {
      const { savePublicSiteSettings } = await import('../api/publicPages');
      await savePublicSiteSettings(routeWorkspaceId, generalSettings);
      setSiteSettings(prev => ({
        ...prev,
        ...generalSettings
      }));
    } catch (error) {
      console.error('❌ 全体設定保存エラー:', error);
      alert(uiText("Failed to save global settings."));
    }
  };

  // 公開ボタン用の処理（バッチ処理版）
  const handlePublish = async () => {
    if (!routeWorkspaceId) {
      console.warn('⚠️ Home.handlePublish: routeWorkspaceIdが未設定');
      return;
    }

    setPublishLoading(true);
    setBatchProgress(null);
    
    try {
      console.time('Home-publishWorkspacePublicPage');

      const { publishWorkspacePublicPage, startBatchPublishContent, getBatchPublishProgress } = await import('../api/publicPages');

      // Step 1: 公開ページの基本設定
      const res = await publishWorkspacePublicPage(routeWorkspaceId);
      console.timeEnd('Home-publishWorkspacePublicPage');

      // 公開URLを設定
      const newPublicUrl = `/cg/${res.publishId}/`;
      setPublicUrl(newPublicUrl);
      setPublicLinkEnabled(true);

      const batchRes = await startBatchPublishContent(routeWorkspaceId);

      if (batchRes.status === 'no_pages') {
        alert(uiText("There were no web pages to generate. In the note list, turn on \"Publish as a web page\" under \"Publish settings\", then run again."));
        setPublishLoading(false);
        return;
      }

      if (batchRes.status === 'already_running') {
        alert(uiText("A process is already running"));
        setPublishLoading(false);
        return;
      }

      if (!batchRes.jobId) {
        throw new Error(uiText("Could not get the job ID"));
      }

      setBatchJobId(batchRes.jobId);

      // 進捗をポーリング開始
      startProgressPolling(batchRes.jobId);
    } catch (error: any) {
      console.error('❌ Home.handlePublish: 公開処理エラー', {
        routeWorkspaceId,
        error,
        errorMessage: error?.message,
        errorCode: error?.code,
        errorStack: error?.stack,
        timestamp: new Date().toISOString()
      });
      alert(error?.message || uiText("Failed to publish"));
      setPublishLoading(false);
      setBatchJobId(null);
    }
  };

  const handleRestoreBoards = async () => {
    if (!routeWorkspaceId || selectedBoardIds.length === 0) return;

    try {
      const updatePromises = selectedBoardIds.map(async (boardId) => {
        const boardRef = doc(db, `workspaces/${routeWorkspaceId}/boards/${boardId}`);
        
        // ボードを復元
        await updateDoc(boardRef, { archived: false });
        
        // ハッシュタグの管理
        try {
          await handleArchivedBoardHashtags(routeWorkspaceId, boardId, false);
        } catch (hashtagError) {
          console.error('ハッシュタグ管理エラー:', hashtagError);
          // ハッシュタグ管理でエラーが発生しても、復元処理は続行
        }
      });

      await Promise.all(updatePromises);

      // 選択状態をリセット
      setSelectedBoardIds([]);
      setIsSelectionMode(false);
      setShowRestoreConfirm(false);

      // ボード一覧を再取得
      refreshBoardList();

      // ハッシュタグ一覧を更新
      refreshHashtags();
    } catch (error) {
      console.error('復元エラー:', error);
    }
  };

  // タブナビゲーション共通ハンドラ（AgentHomePage・ManagementPanel から呼ばれる）
  const handleNavigateTo = openWorkspaceTab;

  // 公開 RAG 管理者フラグ。エージェントタブは本番ビルドでは Free / Starter のみ出す
  const isNcgWorkspace = isNcgSeriesWorkspace;
  const isPublicRAGEnabled = isAdmin && isNcgWorkspace;
  const showAgentTab = isPublicRAGEnabled && isAgentTabVisible(undefined, selectedWorkspace?.planId);
  const canDrs = can('drs');

  const currentTabGroup: 'agent' | 'pages' | 'rag' = (isAgentHomeMode || (isDashboardMode && isAdmin))
    ? 'agent'
    : (isPublicMode || isSuggestMode || isOGAMode || isTokenMode)
      ? 'rag'
      : 'pages';

  const isPageTabActive =
    !isAgentHomeMode &&
    !isHashtagMode &&
    !isFileMode &&
    !isCalendarMode &&
    !isPublicMode &&
    !isSuggestMode &&
    !isOGAMode &&
    !(isDashboardMode && isAdmin) &&
    !isTokenMode &&
    !isThreadMode &&
    (!shouldShowAISearchTab || !isRAGMode) &&
    !isArchiveMode;

  const isAiChatTabActive = shouldShowAISearchTab && isRAGMode;
  // エージェントホーム表示中もサイドバー（ワークスペース親子関係など）を維持する
  const isSidebarEnabled = isPageTabActive || isAiChatTabActive || isAgentHomeMode;

  // 早期 return より前で呼ぶ。空一覧の描画で hooks が減ると React #300 になる
  useEffect(() => {
    if (!isPublicRAGEnabled) {
      prevTabGroupRef.current = null;
      return;
    }
    const prev = prevTabGroupRef.current;
    prevTabGroupRef.current = currentTabGroup;
    if (prev === currentTabGroup) return;
    if (prev === null) {
      if (currentTabGroup === 'rag') {
        setTabGroupOpen((p) => (p.rag ? p : { ...p, rag: true }));
      } else if (currentTabGroup === 'pages' && !isPageTabActive) {
        setTabGroupOpen((p) => (p.pages ? p : { ...p, pages: true }));
      }
      return;
    }
    setTabGroupOpen((p) => (
      p[currentTabGroup] ? p : { ...p, [currentTabGroup]: true }
    ));
  }, [isPublicRAGEnabled, currentTabGroup, isPageTabActive]);

  // ワークスペースがない場合の表示。認証復元前は空一覧と誤判定しない
  if (!authInitializing && !loading && workspaces.length === 0) {
    if (hasNcgApplyPending()) {
      return <Navigate to={NCG_APPLY_PATH} replace />;
    }
    return (
      <Layout>
        <div className="home-container">
          <div style={{ textAlign: 'center', marginTop: '100px' }}>
            <h2>{uiText("You have not joined any workspaces")}</h2>
            <p>{uiText("Create a workspace to manage your notes.")}</p>
            <button
              onClick={() => setShowCreateForm(true)}
              style={{
                padding: '10px 20px',
                fontSize: '16px',
                cursor: 'pointer',
                marginTop: '20px',
                backgroundColor: 'var(--workspace-primary, #cfae91)',
                color: 'white',
                border: 'none',
                borderRadius: '4px'
              }}
            >
              {uiText("Create workspace")}
            </button>
          </div>
        </div>

        {showCreateForm && (
          <NewWorksModal onClose={() => setShowCreateForm(false)} />
        )}
      </Layout>
    );
  }

  const renderDashboardTab = () => (
    <button
      onClick={() => handleNavigateTo('dashboard')}
      style={{
        padding: isMobile ? '8px' : '12px 20px',
        minWidth: isMobile ? '56px' : 'auto',
        backgroundColor: isDashboardMode ? 'var(--workspace-primary, #cfae91)' : 'transparent',
        color: isDashboardMode ? 'white' : 'var(--workspace-primary, #cfae91)',
        border: 'none',
        borderBottom: isDashboardMode ? '3px solid var(--workspace-primary, #cfae91)' : '3px solid transparent',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: isMobile ? '10px' : '16px',
        fontWeight: isDashboardMode ? 'bold' : 'normal',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        gap: isMobile ? '4px' : '0px'
      }}
    >
      {isMobile && (
        <img
          src={publicIcon}
          alt={uiText("Dashboard")}
          style={{
            width: '16px',
            height: '16px',
            filter: isDashboardMode ? 'brightness(0) invert(1)' : 'brightness(0.4) saturate(0) contrast(1.5)'
          }}
        />
      )}
      {uiText("Dashboard")}
    </button>
  );

  return (
    <Layout
      isSidebarDisabled={!isSidebarEnabled}
      showHeaderSearchInput={isPageTabActive}
      sidebarContent={
        isRAGMode ? (
          <AiChatSidebarContent
            onNewChatClick={() => {
              setAiChatActiveSessionId(null);
              setAiChatNewSessionRequest((prev) => prev + 1);
            }}
            onSessionClick={(sessionId) => {
              setAiChatActiveSessionId(sessionId);
              setAiChatSessionSelection((prev) => ({
                sessionId,
                requestId: (prev?.requestId ?? 0) + 1,
              }));
            }}
            selectedSessionId={aiChatActiveSessionId}
            resolvedSessionTitle={aiChatResolvedSessionTitle}
            historyRevalidateRequest={aiChatHistoryRevalidateRequest}
          />
        ) : isAgentHomeMode ? (
          <AgentChatSidebarContent
            workspaceId={routeWorkspaceId ?? ''}
            onNewChatClick={() => {
              setAgentChatSelectedSession(null);
              setAgentChatNewChatRequest((prev) => prev + 1);
            }}
            onSessionClick={(entry) => {
              setAgentChatSelectedSession(entry);
            }}
            selectedSessionId={agentChatSelectedSession?.sessionId ?? null}
          />
        ) : undefined
      }
      onHomeClick={() => {
        handleNavigateTo('home');
      }}
    >
      <div className={`home-container${isAgentHomeMode ? ' home-container--agent' : ''}`}>
        {routeWorkspaceId && isAdmin && (
          <CreditUsageAlert workspaceId={routeWorkspaceId} enabled={isAdmin} />
        )}
        {/* メニュー切り替えボタン */}
        <div
          className={isPublicRAGEnabled ? 'home-tab-bar--grouped' : undefined}
          style={isPublicRAGEnabled ? undefined : {
            display: 'flex',
            marginBottom: '16px',
            borderBottom: '1px solid #e9ecef',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div className={isPublicRAGEnabled ? 'home-tab-groups' : undefined} style={isPublicRAGEnabled ? undefined : { display: 'flex' }}>
            <OptionalHomeTabGroup
              enabled={isPublicRAGEnabled}
              label={uiText("Home")}
              open={tabGroupOpen.agent}
              active={isAgentHomeMode || (isDashboardMode && isAdmin)}
              onToggle={() => setTabGroupOpen((p) => ({ ...p, agent: !p.agent }))}
            >
            {showAgentTab && (
              <button
                onClick={() => handleNavigateTo('home')}
                style={{
                  padding: isMobile ? '8px' : '12px 20px',
                  minWidth: isMobile ? '56px' : 'auto',
                  backgroundColor: isAgentHomeMode ? 'var(--workspace-primary, #cfae91)' : 'transparent',
                  color: isAgentHomeMode ? 'white' : 'var(--workspace-primary, #cfae91)',
                  border: 'none',
                  borderBottom: isAgentHomeMode ? '3px solid var(--workspace-primary, #cfae91)' : '3px solid transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '10px' : '16px',
                  fontWeight: isAgentHomeMode ? 'bold' : 'normal',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  gap: isMobile ? '4px' : '0px',
                }}
              >
                {uiText("Agent")}
              </button>
            )}
            {/* ダッシュボード - NCG はホームグループ。NCN はページが主なのでトークンの直前 */}
            {isAdmin && isNcgWorkspace && renderDashboardTab()}
            </OptionalHomeTabGroup>
            <OptionalHomeTabGroup
              enabled={isPublicRAGEnabled}
              label={uiText("Manage pages")}
              open={tabGroupOpen.pages}
              active={!isAgentHomeMode && currentTabGroup === 'pages'}
              onToggle={() => setTabGroupOpen((p) => ({ ...p, pages: !p.pages }))}
            >
            <button
              onClick={() => handleNavigateTo('page')}
              style={{
                padding: isMobile ? '8px' : '12px 20px',
                minWidth: isMobile ? '56px' : 'auto',
                backgroundColor: !isAgentHomeMode && !isHashtagMode && !isFileMode && !isCalendarMode && !isRAGMode && !isPublicMode && !isSuggestMode && !isOGAMode && !(isDashboardMode && isAdmin) && !isTokenMode && !isThreadMode ? 'var(--workspace-primary, #cfae91)' : 'transparent',
                  color: !isAgentHomeMode && !isHashtagMode && !isFileMode && !isCalendarMode && !isRAGMode && !isPublicMode && !isSuggestMode && !isOGAMode && !(isDashboardMode && isAdmin) && !isTokenMode && !isThreadMode ? 'white' : 'var(--workspace-primary, #cfae91)',
                border: 'none',
                borderBottom: !isAgentHomeMode && !isHashtagMode && !isFileMode && !isCalendarMode && !isRAGMode && !isPublicMode && !isSuggestMode && !isOGAMode && !(isDashboardMode && isAdmin) && !isTokenMode && !isThreadMode ? '3px solid var(--workspace-primary, #cfae91)' : '3px solid transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: isMobile ? '10px' : '16px',
                fontWeight: !isAgentHomeMode && !isHashtagMode && !isFileMode && !isCalendarMode && !isRAGMode && !isPublicMode && !isSuggestMode && !isOGAMode && !(isDashboardMode && isAdmin) && !isTokenMode && !isThreadMode ? 'bold' : 'normal',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                gap: isMobile ? '4px' : '0px'
              }}
            >
              {isMobile && (
                <img 
                  src={noteIcon} 
                  alt={uiText("Home")} 
                  style={{ 
                    width: '16px', 
                    height: '16px',
                    filter: !isAgentHomeMode && !isHashtagMode && !isFileMode && !isCalendarMode && !isRAGMode && !isPublicMode && !isSuggestMode && !isOGAMode && !(isDashboardMode && isAdmin) && !isTokenMode && !isThreadMode ? 'brightness(0) invert(1)' : 'brightness(0.4) saturate(0) contrast(1.5)'
                  }} 
                />
              )}
              {isMobile ? uiText("Home") : uiText("Page")}
            </button>
            <button
              onClick={() => handleNavigateTo('thread')}
              style={{
                padding: isMobile ? '8px' : '12px 20px',
                minWidth: isMobile ? '56px' : 'auto',
                backgroundColor: isThreadMode ? 'var(--workspace-primary, #cfae91)' : 'transparent',
                color: isThreadMode ? 'white' : 'var(--workspace-primary, #cfae91)',
                border: 'none',
                borderBottom: isThreadMode ? '3px solid var(--workspace-primary, #cfae91)' : '3px solid transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: isMobile ? '10px' : '16px',
                fontWeight: isThreadMode ? 'bold' : 'normal',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                gap: isMobile ? '4px' : '0px'
              }}
            >
              {isMobile && (
                <img
                  src={threadIcon}
                  alt={uiText("Thread")}
                  style={{
                    width: '16px',
                    height: '16px',
                    filter: isThreadMode ? 'brightness(0) invert(1)' : 'brightness(0.4) saturate(0) contrast(1.5)'
                  }}
                />
              )}
              {uiText("Thread")}
            </button>
            <button
              onClick={() => handleNavigateTo('calendar')}
              style={{
                padding: isMobile ? '8px' : '12px 20px',
                minWidth: isMobile ? '56px' : 'auto',
                backgroundColor: isCalendarMode ? 'var(--workspace-primary, #cfae91)' : 'transparent',
                  color: isCalendarMode ? 'white' : 'var(--workspace-primary, #cfae91)',
                border: 'none',
                borderBottom: isCalendarMode ? '3px solid var(--workspace-primary, #cfae91)' : '3px solid transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: isMobile ? '10px' : '16px',
                fontWeight: isCalendarMode ? 'bold' : 'normal',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                gap: isMobile ? '4px' : '0px'
              }}
            >
              {isMobile && (
                <img 
                  src={calendarIcon} 
                  alt={uiText("Calendar")} 
                  style={{ 
                    width: '16px', 
                    height: '16px',
                    filter: isCalendarMode ? 'brightness(0) invert(1)' : 'brightness(0.4) saturate(0) contrast(1.5)'
                  }} 
                />
              )}
              {uiText("Calendar")}
            </button>
            {shouldShowAISearchTab && (
              <button
                onClick={() => handleNavigateTo('rag')}
                style={{
                  padding: isMobile ? '8px' : '12px 20px',
                  minWidth: isMobile ? '56px' : 'auto',
                  backgroundColor: isRAGMode ? 'var(--workspace-primary, #cfae91)' : 'transparent',
                    color: isRAGMode ? 'white' : 'var(--workspace-primary, #cfae91)',
                  border: 'none',
                  borderBottom: isRAGMode ? '3px solid var(--workspace-primary, #cfae91)' : '3px solid transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '10px' : '16px',
                  fontWeight: isRAGMode ? 'bold' : 'normal',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  gap: isMobile ? '4px' : '0px'
                }}
              >
                {isMobile && (
                  <img 
                    src={searchIcon} 
                    alt={uiText("AI search")} 
                    style={{ 
                      width: '16px', 
                      height: '16px',
                      filter: isRAGMode ? 'brightness(0) invert(1)' : 'brightness(0.4) saturate(0) contrast(1.5)'
                    }} 
                  />
                )}
                {uiText("AI search")}
              </button>
            )}
            <button
              onClick={() => handleNavigateTo('file')}
              style={{
                padding: isMobile ? '8px' : '12px 20px',
                minWidth: isMobile ? '56px' : 'auto',
                backgroundColor: isFileMode ? 'var(--workspace-primary, #cfae91)' : 'transparent',
                  color: isFileMode ? 'white' : 'var(--workspace-primary, #cfae91)',
                border: 'none',
                borderBottom: isFileMode ? '3px solid var(--workspace-primary, #cfae91)' : '3px solid transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: isMobile ? '10px' : '16px',
                fontWeight: isFileMode ? 'bold' : 'normal',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                gap: isMobile ? '4px' : '0px'
              }}
            >
              {isMobile && (
                <img 
                  src={filesIcon} 
                  alt={uiText("File")} 
                  style={{ 
                    width: '16px', 
                    height: '16px',
                    filter: isFileMode ? 'brightness(0) invert(1)' : 'brightness(0.4) saturate(0) contrast(1.5)'
                  }} 
                />
              )}
              {uiText("File")}
            </button>
            <button
              onClick={() => handleNavigateTo('hashtag')}
              style={{
                padding: isMobile ? '8px' : '12px 20px',
                minWidth: isMobile ? '56px' : 'auto',
                backgroundColor: isHashtagMode ? 'var(--workspace-primary, #cfae91)' : 'transparent',
                color: isHashtagMode ? 'white' : 'var(--workspace-primary, #cfae91)',
                border: 'none',
                borderBottom: isHashtagMode ? '3px solid var(--workspace-primary, #cfae91)' : '3px solid transparent',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: isMobile ? '10px' : '16px',
                fontWeight: isHashtagMode ? 'bold' : 'normal',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                gap: isMobile ? '4px' : '0px'
              }}
            >
              {isMobile && (
                <img 
                  src={hashtagIcon} 
                  alt={uiText("Hashtag")} 
                  style={{ 
                    width: '16px', 
                    height: '16px',
                    filter: isHashtagMode ? 'brightness(0) invert(1)' : 'brightness(0.4) saturate(0) contrast(1.5)'
                  }} 
                />
              )}
              {uiText("Hashtag")}
            </button>
            </OptionalHomeTabGroup>
            <OptionalHomeTabGroup
              enabled={isPublicRAGEnabled}
              label={uiText("RAG settings")}
              open={tabGroupOpen.rag}
              active={currentTabGroup === 'rag'}
              onToggle={() => setTabGroupOpen((p) => ({ ...p, rag: !p.rag }))}
            >
            {/* 公開設定タブ - ワークスペースが公開中かつ管理者のみ表示（最後に移動） */}
            {isNcgWorkspace && isAdmin && (
              <button
                onClick={() => handleNavigateTo('public')}
                style={{
                  padding: isMobile ? '8px' : '12px 20px',
                  minWidth: isMobile ? '56px' : 'auto',
                  backgroundColor: isPublicMode ? 'var(--workspace-primary, #cfae91)' : 'transparent',
                    color: isPublicMode ? 'white' : 'var(--workspace-primary, #cfae91)',
                  border: 'none',
                  borderBottom: isPublicMode ? '3px solid var(--workspace-primary, #cfae91)' : '3px solid transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '10px' : '16px',
                  fontWeight: isPublicMode ? 'bold' : 'normal',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  gap: isMobile ? '4px' : '0px'
                }}
              >
                {isMobile && (
                  <img 
                    src={publicIcon} 
                    alt={uiText("Publish settings")} 
                    style={{ 
                      width: '16px', 
                      height: '16px',
                      filter: isPublicMode ? 'brightness(0) invert(1)' : 'brightness(0.4) saturate(0) contrast(1.5)'
                    }} 
                  />
                )}
                {uiText("Publish settings")}
              </button>
            )}
            {/* DRSタブ - ワークスペースが公開中かつ管理者、かつプランが DRS を含む場合のみ表示 */}
            {isNcgWorkspace && isAdmin && canDrs && (
              <button
                onClick={() => handleNavigateTo('suggest')}
                style={{
                  padding: isMobile ? '8px' : '12px 20px',
                  minWidth: isMobile ? '56px' : 'auto',
                  backgroundColor: isSuggestMode ? 'var(--workspace-primary, #cfae91)' : 'transparent',
                  color: isSuggestMode ? 'white' : 'var(--workspace-primary, #cfae91)',
                  border: 'none',
                  borderBottom: isSuggestMode ? '3px solid var(--workspace-primary, #cfae91)' : '3px solid transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '10px' : '16px',
                  fontWeight: isSuggestMode ? 'bold' : 'normal',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  gap: isMobile ? '4px' : '0px'
                }}
              >
                {isMobile && (
                  <img 
                    src={publicIcon} 
                    alt="DRS" 
                    style={{ 
                      width: '16px', 
                      height: '16px',
                      filter: isSuggestMode ? 'brightness(0) invert(1)' : 'brightness(0.4) saturate(0) contrast(1.5)'
                    }} 
                  />
                )}
                DRS
              </button>
            )}
            {/* OGAタブ - ワークスペースが公開中かつ管理者のみ表示 */}
            {isNcgWorkspace && isAdmin && (
              <button
                onClick={() => handleNavigateTo('oga')}
                style={{
                  padding: isMobile ? '8px' : '12px 20px',
                  minWidth: isMobile ? '56px' : 'auto',
                  backgroundColor: isOGAMode ? 'var(--workspace-primary, #cfae91)' : 'transparent',
                  color: isOGAMode ? 'white' : 'var(--workspace-primary, #cfae91)',
                  border: 'none',
                  borderBottom: isOGAMode ? '3px solid var(--workspace-primary, #cfae91)' : '3px solid transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '10px' : '16px',
                  fontWeight: isOGAMode ? 'bold' : 'normal',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  gap: isMobile ? '4px' : '0px'
                }}
              >
                {isMobile && (
                  <img 
                    src={publicIcon} 
                    alt="OGA" 
                    style={{ 
                      width: '16px', 
                      height: '16px',
                      filter: isOGAMode ? 'brightness(0) invert(1)' : 'brightness(0.4) saturate(0) contrast(1.5)'
                    }} 
                  />
                )}
                OGA
              </button>
            )}
            {/* NCN はページ活用が主。ダッシュボードは利用状況としてトークンの直前 */}
            {isAdmin && !isNcgWorkspace && renderDashboardTab()}
            {/* 利用トークンタブ - 管理者のみ表示（メンバーには非表示） */}
            {isAdmin && (
              <button
                onClick={() => handleNavigateTo('token')}
                style={{
                  padding: isMobile ? '8px' : '12px 20px',
                  minWidth: isMobile ? '56px' : 'auto',
                  backgroundColor: isTokenMode ? 'var(--workspace-primary, #cfae91)' : 'transparent',
                  color: isTokenMode ? 'white' : 'var(--workspace-primary, #cfae91)',
                  border: 'none',
                  borderBottom: isTokenMode ? '3px solid var(--workspace-primary, #cfae91)' : '3px solid transparent',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '10px' : '16px',
                  fontWeight: isTokenMode ? 'bold' : 'normal',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  gap: isMobile ? '4px' : '0px'
                }}
              >
                {isMobile && (
                  <img 
                    src={publicIcon} 
                    alt={uiText("Tokens used")} 
                    style={{ 
                      width: '16px', 
                      height: '16px',
                      filter: isTokenMode ? 'brightness(0) invert(1)' : 'brightness(0.4) saturate(0) contrast(1.5)'
                    }} 
                  />
                )}
                {uiText("Tokens used")}
              </button>
            )}
            </OptionalHomeTabGroup>
          </div>
        </div>

        {/* 選択モード時のアクションバー */}
        {!isAgentHomeMode && !isHashtagMode && !isFileMode && !isCalendarMode && !isPublicMode && !isSuggestMode && !isOGAMode && !(isDashboardMode && isAdmin) && !isTokenMode && !isThreadMode && (!shouldShowAISearchTab || !isRAGMode) && isSelectionMode && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
            padding: '12px 16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '14px', color: '#495057' }}>
                {uiText("Now selected:")} {selectedBoardIds.length}{uiText(" items")}
              </span>
              <button
                onClick={handleSelectAll}
                style={{
                  padding: '6px 12px',
                  backgroundColor: selectedBoardIds.length === currentBoards.length ? '#6c757d' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = selectedBoardIds.length === currentBoards.length ? '#5a6268' : '#0056b3';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = selectedBoardIds.length === currentBoards.length ? '#6c757d' : '#007bff';
                }}
              >
                {selectedBoardIds.length === currentBoards.length ? uiText("Clear every selection") : uiText("Select everything")}
              </button>
              {isArchiveMode ? (
                <button
                  onClick={() => setShowRestoreConfirm(true)}
                  disabled={selectedBoardIds.length === 0}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: selectedBoardIds.length === 0 ? '#ccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedBoardIds.length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '12px'
                  }}
                >
                  {uiText("Restore")}
                </button>
              ) : (
                <button
                  onClick={() => setShowArchiveConfirm(true)}
                  disabled={selectedBoardIds.length === 0}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: selectedBoardIds.length === 0 ? '#ccc' : 'var(--workspace-primary, #cfae91)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: selectedBoardIds.length === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '12px'
                  }}
                >
                  {uiText("Archive")}
                </button>
              )}
              {/* 公開・非公開ボタン - ワークスペースが公開されている時のみ表示 */}
              {isNcgWorkspace && isAdmin && (
                <>
                  {/* 進捗表示 */}
                  {isPublishing && publishProgress > 0 && (
                    <div
                      style={{
                        marginLeft: '8px',
                        marginBottom: '8px',
                        width: '200px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '4px',
                        }}
                      >
                        <span style={{ fontSize: '12px', color: '#666' }}>{uiText("Applying to AI answers...")}</span>
                        <span style={{ fontSize: '12px', color: '#666' }}>{publishProgress}%</span>
                      </div>
                      <div
                        style={{
                          width: '100%',
                          height: '4px',
                          backgroundColor: '#e2e8f0',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${publishProgress}%`,
                            height: '100%',
                            backgroundColor: '#28a745',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                      <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                        {uiText("If there are many pages, this can take a while to finish")}
                      </div>
                    </div>
                  )}
                  <PublishAxesMenu
                    open={showPublishMenu}
                    onOpenChange={setShowPublishMenu}
                    disabled={selectedBoardIds.length === 0}
                    busy={isPublishing || isUnpublishing || isSettingWebEnabled}
                    selectedCount={selectedPublishAxes.count}
                    ragState={selectedPublishAxes.rag}
                    webState={selectedPublishAxes.web}
                    htmlPagesEnabled={htmlPagesEnabledForWs}
                    canUsePublishSettings={can('publicRag')}
                    canPublishWebPages={can('publicRag.webPages')}
                    onApply={(next) => { void handleApplyPublishAxes(next); }}
                  />
                  {/* 非公開進捗表示 */}
                  {isUnpublishing && unpublishProgress > 0 && (
                    <div
                      style={{
                        marginLeft: '4px',
                        marginBottom: '8px',
                        width: '200px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '4px',
                        }}
                      >
                        <span style={{ fontSize: '12px', color: '#666' }}>{uiText("Removing from the answer...")}</span>
                        <span style={{ fontSize: '12px', color: '#666' }}>{unpublishProgress}%</span>
                      </div>
                      <div
                        style={{
                          width: '100%',
                          height: '4px',
                          backgroundColor: '#e2e8f0',
                          borderRadius: '2px',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${unpublishProgress}%`,
                            height: '100%',
                            backgroundColor: '#dc3545',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                      <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                        {uiText("If there are many pages, this can take a while to finish")}
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* フォルダーへ移動ボタン */}
              <button
                onClick={() => setFolderMoveModalOpen(true)}
                disabled={selectedBoardIds.length === 0 || isMovingToFolder}
                style={{
                  padding: '6px 12px',
                  backgroundColor: selectedBoardIds.length === 0 || isMovingToFolder ? '#ccc' : '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: selectedBoardIds.length === 0 || isMovingToFolder ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  marginLeft: '8px'
                }}
              >
                {isMovingToFolder ? uiText("Moving...") : uiText("Move to folder")}
              </button>
              {/* エクスポート（ダウンロード）ボタン */}
              {routeWorkspaceId && (
                <ExportSelectedButton
                  workspaceId={routeWorkspaceId}
                  selectedPageIds={selectedBoardIds}
                  disabled={selectedBoardIds.length === 0}
                />
              )}
            </div>
            <button
              onClick={handleSelectionModeToggle}
              style={{
                padding: '6px 12px',
                backgroundColor: '#ff6b6b',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              {uiText("Cancel selection")}
            </button>
          </div>
        )}

        {/* ページ一覧上部：ソート・メニューボタン（ページタブのときのみ） */}
        {!isAgentHomeMode && !isHashtagMode && !isFileMode && !isCalendarMode && !isPublicMode && !isSuggestMode && !isOGAMode && !(isDashboardMode && isAdmin) && !isTokenMode && !isThreadMode && (!shouldShowAISearchTab || !isRAGMode) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '8px', gap: '12px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: 'var(--workspace-border-soft, #f5f0eb)',
                borderRadius: '8px',
                padding: '4px 6px',
                border: 'none',
              }}
            >
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'lastModified' | 'createdAt' | 'title' | 'contentSize')}
                style={{
                  height: '32px',
                  padding: '0 10px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#333',
                  backgroundColor: 'var(--workspace-border-soft, #f5f0eb)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              >
                <option value="lastModified">{uiText("Last edited")}</option>
                <option value="createdAt">{uiText("Created at")}</option>
                <option value="title">{uiText("Title")}</option>
                <option value="contentSize">{uiText("Data volume")}</option>
              </select>
              <button
                type="button"
                onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--workspace-primary-dark, #b8956e)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                }}
                style={{
                  height: '32px',
                  padding: '0 12px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#fff',
                  backgroundColor: 'var(--workspace-primary, #cfae91)',
                  border: 'none',
                  borderLeft: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                  transition: 'background-color 0.15s ease',
                  outline: 'none',
                }}
                title={sortOrder === 'desc' ? uiText("Descending (newest first)") : uiText("Ascending (oldest first)")}
              >
                {sortOrder === 'desc' ? uiText("↓ Descending") : uiText("↑ Ascending")}
              </button>
            </div>
            <div className="menu-container" style={{ position: 'relative' }}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                style={{
                  padding: '8px',
                  backgroundColor: 'var(--workspace-border-soft, #f5f0eb)',
                  color: 'var(--workspace-primary, #cfae91)',
                  border: 'none',
                  borderBottom: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  outline: 'none',
                }}
                title={uiText("Menu")}
              >
                ⋮
              </button>
              <HomeMenu
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                isSelectionMode={isSelectionMode}
                onSelectionModeToggle={handleSelectionModeToggle}
                onOpenImport={() => setImportModalOpen(true)}
                isArchiveMode={isArchiveMode}
                onArchiveModeToggle={() => {
                  setIsArchiveMode(!isArchiveMode);
                  if (!isArchiveMode) {
                    setSelectedHashtag(null);
                    setSearchQuery('');
                    setIsSearching(false);
                  }
                }}
                onOpenDeleteUntitled={() => setDeleteUntitledModalOpen(true)}
                onOpenBackup={() => setBackupModalOpen(true)}
              />
            </div>
          </div>
        )}

        {/* メインコンテンツ */}
        <div
          className={isAgentHomeMode ? 'home-main-agent' : undefined}
          style={{ marginTop: isAgentHomeMode ? 0 : '20px' }}
        >
          {(() => {
            if (isAgentHomeMode && showAgentTab) {
              // エージェントホーム
              return (
                <AgentHomePage
                  workspaceId={routeWorkspaceId ?? ''}
                  isAdmin={isAdmin}
                  onNavigateTo={handleNavigateTo}
                  publishEnabled={isNcgWorkspace}
                  publishedPages={publishedPages}
                  standbyPages={standbyPages}
                  publicUrl={publicUrl}
                  publicLinkEnabled={publicLinkEnabled}
                  publicLinkReady={publicLinkReady}
                  onPublicLinkEnabledChange={setPublicLinkEnabled}
                  selectedSession={agentChatSelectedSession}
                  newChatRequest={agentChatNewChatRequest}
                  onSessionSaved={(entry) => {
                    setAgentChatSelectedSession(entry);
                  }}
                />
              );
            }
            if (isCalendarMode) {
              // 選択された日付のボードを取得
              const getSelectedDateBoards = () => {
                if (!selectedDate) {
                  return [];
                }

                const year = selectedDate.getFullYear();
                const month = selectedDate.getMonth() + 1;
                const day = selectedDate.getDate();

                const parseDateFromStructured = (previewData: any): { year: number; month: number; day: number } | null => {
                  try {
                    const sd = previewData?.structuredData;
                    const iso: string | undefined = sd?.datePublished || sd?.eventDate;
                    if (!iso || typeof iso !== 'string') return null;
                    let d: Date;
                    if (iso.includes('T') || iso.includes('Z')) {
                      d = new Date(iso);
                    } else if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
                      d = new Date(iso + 'T00:00:00');
                    } else {
                      d = new Date(iso);
                    }
                    if (isNaN(d.getTime())) return null;
                    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
                  } catch (_) {
                    return null;
                  }
                };

                const matchingBoards = boards.filter(board => {
                  const searchData = board.searchData as any;
                  if (searchData?.DateOnPage) {
                    const dateInfo = searchData.DateOnPage;
                    const matches = dateInfo.year === year && dateInfo.month === month && dateInfo.day === day;
                    if (matches) {}
                    return matches;
                  }
                  const fallback = parseDateFromStructured((board as any).previewData);
                  if (fallback) {
                    const matches = fallback.year === year && fallback.month === month && fallback.day === day;
                    if (matches) {}
                    return matches;
                  }
                  return false;
                });

                return matchingBoards;
              };

              const selectedDateBoards = getSelectedDateBoards();

              return (
                <div>
                  {/* カレンダーコンポーネント */}
                  {calendarLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      {uiText("Loading now...")}
                    </div>
                  ) : calendarError ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#dc3545' }}>
                      {uiText("Failed to load calendar data")}
                    </div>
                  ) : (
                    <CalendarComponent 
                      dateData={dateData} 
                      boards={boards}
                      onDateSelect={setSelectedDate}
                      selectedDate={selectedDate}
                    />
                  )}

                  {/* 選択された日付のボードプレビュー */}
                  {selectedDate && selectedDateBoards.length > 0 && (
                    <div style={{ marginTop: '30px' }}>
                      <h3 style={{ 
                        fontSize: '18px', 
                        fontWeight: 'bold', 
                        color: '#333',
                        marginBottom: '15px'
                      }}>
                        {selectedDate.toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          weekday: 'long'
                        })} {uiText(" pages")}
                      </h3>
                      
                       <div style={{
                         display: 'grid',
                         gridTemplateColumns: isMobile 
                           ? 'repeat(2, 1fr)' 
                           : 'repeat(auto-fill, minmax(180px, 1fr))',
                         gap: isMobile ? '8px' : '16px'
                       }}>
                        {selectedDateBoards.map((board) => (
                          <NotePreviewCard
                            key={board.id}
                            id={board.id}
                            title={board.title}
                            content={board.content}
                            firstImageUrl={board.firstImageUrl}
                            lastModified={board.lastModified}
                            slug={board.slug}
                            pinned={board.pinned}
                            archived={board.archived}
                            isMobile={isMobile}
                            onCardClick={(slug) => {
                              if (!routeWorkspaceId) return;
                              navigate(workspacePagePath(routeWorkspaceId, slug));
                            }}
                            hashtags={board.hashtags}
                            previewData={board.previewData}
                            isCrawled={board.isCrawled}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            } else if (isHashtagMode) {
              return (
                <HashtagListView 
                  onHashtagSelect={() => setIsHashtagMode(false)}
                />
              );
            } else if (isFileMode) {
              return (
                <FileListView 
                  refreshTrigger={refreshTrigger}
                />
              );
            } else if (isArchiveMode) {
              return (
                <ArchivedBoardList
                  isSelectionMode={isSelectionMode}
                  onSelectionChange={setSelectedBoardIds}
                  selectedBoardIds={selectedBoardIds}
                  refreshTrigger={refreshTrigger}
                  sortOrder={sortOrder}
                  sortBy={sortBy}
                />
              );
            } else if (isPublicMode) {
              // 公開設定タブのコンテンツ
              return (
                <Suspense fallback={<TabLoading label={uiText("Publish settings")} />}>
                  {routeWorkspaceId ? (
                    <LazyPublicSettings
                      workspaceId={routeWorkspaceId}
                      workspaceName={selectedWorkspace?.name || ''}
                      ogTitle={ogTitle}
                      publicUrl={publicLinkEnabled ? publicUrl : ''}
                      publishLoading={publishLoading}
                      totalPages={totalPages}
                      standbyPages={standbyPages}
                      publishedPages={publishedPages}
                      batchProgress={batchProgress}
                      batchUnpublishProgress={batchUnpublishProgress}
                      batchJobId={batchJobId}
                      batchUnpublishJobId={batchUnpublishJobId}
                      siteSettings={siteSettings}
                      siteSettingsLoading={siteSettingsLoading}
                      onPublish={handlePublish}
                      onFaviconChange={handleFaviconChange}
                      onTabTitleChange={handleTabTitleChange}
                      onHeaderSettingsChange={handleHeaderSettingsChange}
                      onGeneralSettingsChange={handleGeneralSettingsChange}
                      onContentSettingsChange={handleContentSettingsChange}
                      onFooterSettingsChange={handleFooterSettingsChange}
                      onOpenDrsKeywords={openDrsKeywordTab}
                    />
                  ) : (
                    <div style={{ padding: 16 }}>{uiText("Loading workspace...")}</div>
                  )}
                </Suspense>
              );
            } else if (isSuggestMode && canDrs) {
              // DRSタブのコンテンツ
              return (
                <Suspense fallback={<TabLoading label="DRS" />}>
                  {routeWorkspaceId ? (
                    <LazyDRSSection
                      openSubTab={drsOpenSubTab || undefined}
                      openRequestId={drsOpenRequestId}
                    />
                  ) : (
                    <div style={{ padding: 16 }}>{uiText("Loading workspace...")}</div>
                  )}
                </Suspense>
              );
            } else if (isDashboardMode && isAdmin) {
              // ダッシュボードタブのコンテンツ
              return (
                <Suspense fallback={<TabLoading label={uiText("Analytics")} />}>
                  {routeWorkspaceId ? (
                    <LazyRAGAnalyticsDashboard
                      workspaceId={routeWorkspaceId}
                      showPublicRAG={isNcgWorkspace && isAdmin}
                      onCreatePage={(title) => {
                        const encodedTitle = encodeURIComponent(title);
                        navigate(`/${routeWorkspaceId}/new?title=${encodedTitle}`, { replace: false });
                      }}
                    />
                  ) : (
                    <div style={{ padding: 16 }}>{uiText("Loading workspace...")}</div>
                  )}
                </Suspense>
              );
            } else if (isThreadMode) {
              return (
                <Suspense fallback={<TabLoading label="Threads" />}>
                  {routeWorkspaceId ? (
                    <LazyThreadsTabView
                      workspaceId={routeWorkspaceId}
                      isMobileOrTablet={isMobileOrTablet}
                      initialSelectedThreadId={openThreadId}
                    />
                  ) : (
                    <div style={{ padding: 16 }}>{uiText("Loading workspace...")}</div>
                  )}
                </Suspense>
              );
            } else if (isTokenMode) {
              // 利用トークンタブのコンテンツ
              return (
                <Suspense fallback={<TabLoading label={uiText("Token usage")} />}>
                  {routeWorkspaceId ? (
                    <LazyTokenUsageDashboard workspaceId={routeWorkspaceId} />
                  ) : (
                    <div style={{ padding: 16 }}>{uiText("Loading workspace...")}</div>
                  )}
                </Suspense>
              );
            } else if (isOGAMode) {
              // OGAタブのコンテンツ
              return (
                <Suspense fallback={<TabLoading label="OGA" />}>
                  {routeWorkspaceId ? (
                    <LazyOGASetting
                      workspaceId={routeWorkspaceId}
                      boardId={undefined}
                      title={undefined}
                    />
                  ) : (
                    <div style={{ padding: 16 }}>{uiText("Loading workspace...")}</div>
                  )}
                </Suspense>
              );
            } else {
              return null;
            }
          })()}
          {/* BoardList は常にマウントし、別タブ時は display:none で隠す（タブ切替の即時表示のため） */}
          <div style={{
            display: (isAgentHomeMode || isHashtagMode || isFileMode || isCalendarMode || isRAGMode || isPublicMode || isSuggestMode || isOGAMode || (isDashboardMode && isAdmin) || isTokenMode || isThreadMode || isArchiveMode)
              ? 'none' : undefined,
          }}>
            <BoardList
              isSelectionMode={isSelectionMode}
              onSelectionChange={setSelectedBoardIds}
              selectedBoardIds={selectedBoardIds}
              refreshTrigger={refreshTrigger}
              onBoardsChange={setCurrentBoards}
              sortOrder={sortOrder}
              sortBy={sortBy}
              htmlPagesEnabled={htmlPagesEnabledForWs}
            />
          </div>
          {/* RAGSearchInterface は常にマウントし、AI検索タブ以外は display:none で隠す（タブ切替で状態を保持するため） */}
          {shouldShowAISearchTab && (
            <div style={{
              display: isRAGMode ? undefined : 'none',
              maxWidth: '1200px',
              margin: '0 auto',
              padding: '0',
            }}>
              <RAGSearchInterface
                onError={(error) => {
                  console.error('RAG検索エラー:', error);
                  alert(uiText("An error occurred while searching: ") + error);
                }}
                className="rag-search-home"
                externalNewChatRequest={aiChatNewSessionRequest}
                externalSessionSelection={aiChatSessionSelection}
                onSessionHistoryTitleResolved={(entry) => {
                  setAiChatActiveSessionId(entry.sessionId);
                  setAiChatResolvedSessionTitle((prev) => ({
                    ...entry,
                    requestId: (prev?.requestId ?? 0) + 1,
                  }));
                }}
                onSessionHistoryRevalidate={() => {
                  setAiChatHistoryRevalidateRequest((prev) => prev + 1);
                }}
              />
            </div>
          )}
        </div>

        {/* 右下固定の新規作成ボタン */}
        {!isAgentHomeMode && !isHashtagMode && !isFileMode && !isCalendarMode && !isPublicMode && !isSuggestMode && !isOGAMode && !(isDashboardMode && isAdmin) && !isTokenMode && !isThreadMode && (!shouldShowAISearchTab || !isRAGMode) && (
          <div
            style={{
              position: 'fixed',
              bottom: isMobile ? '16px' : '24px',
              right: isMobile ? '16px' : '24px',
              zIndex: 1000,
            }}
          >
          <button
            onClick={() => {
              // 現在のURLパラメータを取得して embed=true を引き継ぐ
              const currentParams = new URLSearchParams(window.location.search);
              const embedParam = currentParams.get('embed');
              
              if (!routeWorkspaceId) return;
              let url = selectedHashtag
                ? `/${routeWorkspaceId}/new?hashtag=${encodeURIComponent(selectedHashtag)}`
                : `/${routeWorkspaceId}/new`;
              
              // embed=true がある場合は引き継ぐ
              if (embedParam === 'true') {
                url += url.includes('?') ? '&embed=true' : '?embed=true';
              }
              
              // 同じURLへの遷移の場合は強制的にページを更新（念のため）
              const isSameUrl = window.location.pathname === url.split('?')[0];
              if (isSameUrl) {
                // 同じURLの場合は一度リロードして確実に初期化
                window.location.href = url;
                return;
              }
              
              navigate(url, { replace: false }); // 新規作成時は履歴に残す
            }}
            style={{
              width: isMobile ? '48px' : '56px',
              height: isMobile ? '48px' : '56px',
              borderRadius: '12px',
              backgroundColor: 'var(--workspace-primary, #cfae91)',
              color: '#fff',
              fontSize: isMobile ? '28px' : '32px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              border: 'none',
              outline: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
            }}
            title={uiText("Create new")}
          >
            <img 
              src={newpageIcon} 
              alt={uiText("Create new")} 
              style={{ 
                width: isMobile ? '36px' : '40px', 
                height: isMobile ? '36px' : '40px',
                filter: 'brightness(0) invert(1)'
              }} 
            />
          </button>
          </div>
        )}

        {/* アーカイブ確認モーダル */}
        {showArchiveConfirm && (
          <div
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
            onClick={() => setShowArchiveConfirm(false)}
          >
            <div
              style={{
                backgroundColor: '#fff',
                padding: '24px',
                borderRadius: '8px',
                minWidth: '320px',
                maxWidth: '90%',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ marginTop: 0, marginBottom: '16px' }}>
                {uiText("Confirm archive")}
              </h3>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                {uiText("Selected")} {selectedBoardIds.length} {uiText("pages be archived?")}
                <br />
                {uiText("Archived pages are hidden from the list.")}
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowArchiveConfirm(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#ccc',
                    color: '#000',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {uiText("Cancel")}
                </button>
                <button
                  onClick={handleArchiveBoards}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--workspace-primary, #cfae91)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {uiText("Archive")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 復元確認モーダル */}
        {showRestoreConfirm && (
          <div
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
            }}
            onClick={() => setShowRestoreConfirm(false)}
          >
            <div
              style={{
                backgroundColor: '#fff',
                padding: '24px',
                borderRadius: '8px',
                minWidth: '320px',
                maxWidth: '90%',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ marginTop: 0, marginBottom: '16px' }}>
                {uiText("Confirm restore")}
              </h3>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                {uiText("Selected")} {selectedBoardIds.length} {uiText("pages be restored?")}
                <br />
                {uiText("Restored pages appear in the regular page list.")}
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowRestoreConfirm(false)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#ccc',
                    color: '#000',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {uiText("Cancel")}
                </button>
                <button
                  onClick={handleRestoreBoards}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {uiText("Restore")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 空のMemoページ削除モーダル */}
        <DeleteUntitledModal
          isOpen={deleteUntitledModalOpen}
          onClose={() => setDeleteUntitledModalOpen(false)}
          onConfirm={handleDeleteUntitledPages}
        />

        {/* バックアップ一覧モーダル */}
        {backupModalOpen && routeWorkspaceId && (
          <BackupModal
            onClose={() => setBackupModalOpen(false)}
          />
        )}

        {/* フォルダー移動モーダル */}
        <FolderMoveModal
          isOpen={folderMoveModalOpen}
          onClose={() => setFolderMoveModalOpen(false)}
          onConfirm={handleMoveToFolder}
          selectedPageCount={selectedBoardIds.length}
        />

        {/* ページインポートモーダル */}
        {routeWorkspaceId && (
          <ImportPagesModal
            isOpen={importModalOpen}
            onClose={() => setImportModalOpen(false)}
            workspaceId={routeWorkspaceId}
            onImported={() => {
              refreshBoardList();
              void queryClient.invalidateQueries({ queryKey: ['page-sidebar-folder-counts', routeWorkspaceId] });
            }}
          />
        )}

      </div>
      {/* ManagementPanel: 管理者向けドロワー */}
      <ManagementPanel
        isOpen={isManagementPanelOpen}
        onClose={() => setIsManagementPanelOpen(false)}
        onNavigateTo={handleNavigateTo}
        isAdmin={isAdmin}
        isPublishEnabled={isNcgWorkspace && isAdmin}
      />
    </Layout>
  );
};

export default Home;
